/**
 * WorkProfileOverview
 * ---------------------------------------------------------------------------
 * Premium enterprise "Project Work Profile" dashboard — the default landing
 * view inside the WorkProfile modal. Full-width, no side navigation.
 *
 * Everything is driven by real work data:
 *  • Funding pattern  → fundingPatternApi (CAPEX/OPEX × source matrix)
 *  • Components       → worksApi.getBoqBreakdown (Types of Work with planned/
 *                       actual dates, amounts, sub-activities and progress)
 *  • Map + photos     → workGisApi geometries + geo-tagged progress photos
 *
 * Stack matches the codebase — TailwindCSS, custom SVG charts (DonutChart),
 * Remix icons (ri-*), an embedded Leaflet map and CSS transitions.
 */

import '@/modules/utility/ds-wms/styles/wmsTable.css';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Skeleton } from '@/components/base/Skeleton';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import DonutChart from '../dashboard/DonutChart';
import { renderDualProgress } from '../dashboard/progressRenderers';
import { fundingPatternApi, attachmentApi, workGisApi, worksApi, type FundingPattern } from '../../services/wmsService';
import { fetchUdaBoundary, drawUdaBoundary } from '../../lib/udaBoundary';
import { TILE_URLS } from '@/config/mapTiles';
import ContractorLink from '../ContractorLink';
import { WMS_POPUP_HEADER, WMS_POPUP_ICON, WMS_POPUP_TITLE, WMS_POPUP_SUBTITLE, WMS_POPUP_CLOSE } from '../common/wmsPopupHeader';

// ── Palette (per design spec) ──────────────────────────────────────────────
const C = {
  purple: '#6D28D9',
  gradient: 'linear-gradient(135deg, #4C1D95 0%, #6D28D9 50%, #8B5CF6 100%)',
  success: '#16A34A',
  warning: '#F59E0B',
  danger: '#EF4444',
  info: '#3B82F6',
  textPrimary: '#111827',
  textSecondary: '#6B7280',
  border: '#E5E7EB',
  bg: '#F8FAFC',
};

// ── Component (milestone) shape passed from WorkProfile (fallback source) ────
export interface OverviewComponent {
  name: string;
  progress: number;
  subCount: number;
  started: boolean;
  startDate?: string;
  endDate?: string;
  actualStart?: string;
  status?: string;
  weightage?: number;
  plndAmtRupees?: number | null;
}

interface RawDesc {
  name: string;
  physical: number;
  subCount: number;
  started: boolean;
  startDate: string;
  endDate: string;
  amountRupees: number;
  wrkBoqId?: number;
  /** Schedule target % due by today (Σ plan × elapsed-month-fraction ÷ Σ plan). */
  scheduleTarget?: number | null;
  /** Total planned value (Σ typeAmt × plan%) — the weight for rolling descriptions
   *  up to the work-level Overall Work physical/target. */
  planValue?: number;
}

export interface WorkOverviewData {
  workCode: string;
  workName: string;
  status: string;
  workType: string;
  scheme?: string;
  description?: string;
  /** Headquarters point of the work's own authority. Used only as a map fallback when
   *  the work itself has no coordinate — never presented as the work's location. */
  udaCentre?: { lat: number; lng: number; town?: string | null } | null;
  /** Body that physically executed the work — often not the funding authority.
   *  Undefined/empty means not recorded, which is NOT the same as "the authority". */
  executingAgency?: string;
  /** Body that ends up owning the asset, where it differs from funder and executor. */
  owningBody?: string;
  location: {
    district?: string; municipality?: string; ward?: string; village?: string;
    address?: string; lat?: number | string; lng?: number | string; nearestHabitation?: string;
  };
  engineer: { name?: string; designation?: string; department?: string; phone?: string };
  ee?: { name?: string; phone?: string };
  se?: { name?: string; phone?: string };
  agreement?: { no?: string; date?: string; endDate?: string };
  contractor: { id?: number | null; name?: string; code?: string; phone?: string };
  /** null = never recorded in the source, rendered as a dash. 0 = genuinely zero. */
  projectCostCr: number | null;
  financial: {
    contractValueCr: number | null; completedValueCr: number | null; paidCr: number;
    pendingBillsCr: number; sanctionedCr: number | null; releasedCr?: number | null;
  };
  dates: { startDate?: string; expectedCompletion?: string };
  physicalProgress: number;
  financialProgress: number;
  components: OverviewComponent[];
  counts?: { issues?: number; criticalIssues?: number };
}

interface Props {
  workId?: number | string;
  data: WorkOverviewData;
  onDownloadPdf?: () => void;
  onPrint?: () => void;
  onOpenFullMap?: () => void;
  onEditFunding?: () => void;
  exportingPdf?: boolean;
}

// ── Formatting helpers ──────────────────────────────────────────────────────
/** Values are held in crore. Rendering everything as crore hides small figures — a
 *  ₹68,712 work read "₹0.01 Cr", which is useless. Scale the unit to the amount so the
 *  real number is always legible: crore >= ₹1 Cr, lakh >= ₹1 L, plain rupees below.
 *  null/undefined means never recorded and shows a dash; a genuine zero shows ₹0.00 Cr. */
const fmtCr = (v?: number | null) => {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  const n = Number(v);
  if (n === 0) return '₹0.00 Cr';
  const two = (x: number) => x.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const abs = Math.abs(n);
  if (abs >= 1) return `₹${two(n)} Cr`;
  if (abs >= 0.01) return `₹${two(n * 100)} L`;
  return `₹${Math.round(n * 1e7).toLocaleString('en-IN')}`;
};
const fmtDate = (s?: string) => {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};
const clamp = (n: number) => Math.max(0, Math.min(100, Math.round(n)));
const num = (v: any) => parseFloat(v as any) || 0;
/* Rupees -> crores. One divisor, no inference.
 *
 * This used to GUESS the unit from the magnitude:
 *     return n >= 1e5 ? n / 1e7 : n;
 * "big number = rupees, small number = already crores". Any genuine rupee amount
 * below Rs 1,00,000 therefore passed through untouched and was then rendered with a
 * "Cr" suffix, inflating it by 10,000,000x. Observed on WRK/2026-27/0008:
 *
 *   BOQ "Road marking, signage and safety furniture"  Rs    90,000  ->  "Rs 90,000.00 Cr"
 *   Value of work done                                Rs    10,290  ->  "10290.00 Cr"
 *   Utilization  = 10290 / 0.49 * 100                            ->  "2100000.00%"
 *   Balance      = max(0.49 - 10290, 0)                          ->  "0.00 Cr"  (true: ~0.49 Cr)
 *
 * The three larger BOQ lines on the same screen converted correctly, which is what
 * made this survive: the heuristic is right for most values and silently catastrophic
 * for small ones, so the card looked plausible apart from one row.
 *
 * The guess existed because two units really do arrive from the server. That is fixed
 * at the source instead: every caller now passes a RUPEE value. The one field that was
 * denominated in crores (cstm_flds_jn.paid_to_contractor) is no longer read - its
 * rupee twin paid_rupees is used, which dashboard.service.js documents as the exact
 * figure and which is also the more complete column (823 works vs 817).
 *
 * Any future caller must pass rupees. A value already in crores will now be divided
 * again and read as ~0.00, which is visibly wrong rather than quietly wrong - the
 * failure mode is the right way round. */
const toCr = (v: any) => (Number(v) || 0) / 1e7;
const progressColor = (p: number) => (p >= 70 ? C.success : p >= 40 ? C.warning : C.danger);
const daysBetween = (a: Date, b: Date) => Math.round((b.getTime() - a.getTime()) / 86400000);

// Realistic photo per component type (keyword → loremflickr; deterministic).
const COMPONENT_IMG: Array<{ re: RegExp; kw: string; icon: string }> = [
  { re: /treat|wtp|filtr|clarif|head\s*works/i, kw: 'water,treatment,plant', icon: 'ri-drop-fill' },
  { re: /pump|raw\s*water|intake|jack\s*well/i, kw: 'water,pumping,station', icon: 'ri-home-gear-fill' },
  { re: /transmiss|rising|gravity|feeder|convey|main\b|pipe.*lay|laying|pipe\s*line|pipeline/i, kw: 'pipeline,construction,water', icon: 'ri-route-line' },
  { re: /esr|gls|reservoir|ohsr|sump|tank|tower|storage|balanc/i, kw: 'water,tower,tank', icon: 'ri-database-2-fill' },
  { re: /distrib|network|reticul|hsc|house\s*service|connection/i, kw: 'water,pipe,network', icon: 'ri-node-tree' },
  { re: /electr|scada|e\s*&\s*m|mechanical|automation|panel|instrument/i, kw: 'electrical,control,panel', icon: 'ri-flashlight-fill' },
  { re: /road|csp|cc\s*road|drain|culvert|civil/i, kw: 'road,construction,site', icon: 'ri-government-line' },
];
const componentVisual = (name: string, idx: number) => {
  const hit = COMPONENT_IMG.find((x) => x.re.test(name || '')) || { kw: 'water,supply,construction', icon: 'ri-building-3-fill' };
  return { img: `https://loremflickr.com/600/400/${hit.kw}?lock=${idx + 11}`, icon: hit.icon };
};

// Flatten every coordinate pair out of arbitrary GeoJSON → [[lng,lat], ...].
const collectCoords = (g: any, out: number[][]) => {
  if (!g) return;
  if (Array.isArray(g)) {
    if (typeof g[0] === 'number' && typeof g[1] === 'number') { out.push([g[0], g[1]]); return; }
    g.forEach((x) => collectCoords(x, out));
    return;
  }
  if (g.type === 'FeatureCollection') { (g.features || []).forEach((f: any) => collectCoords(f, out)); return; }
  if (g.type === 'Feature') { collectCoords(g.geometry, out); return; }
  if (g.coordinates) collectCoords(g.coordinates, out);
};
const parseGeom = (g: any) => { if (!g) return null; if (typeof g === 'string') { try { return JSON.parse(g); } catch { return null; } } return g; };

// ── Mobile geo-tag coordinate normalizers ───────────────────────────────────
// Field-captured geo-tags (from the app) store their coordinates loosely: a point
// can be {lat,lng} / {latitude,longitude} / [lat,lng] / [lng,lat]; a path can be a
// bare coordinate array or an object wrapping one. These coerce whatever shape the
// app sent into Leaflet [lat, lng] pairs.
//
// India bounding box (lat 6–38, lng 68–98). Every work is in Andhra Pradesh /
// Telangana, so a coordinate outside India is junk — most often the Android
// emulator's default fix (37.4220, -122.0840, Googleplex) recorded during testing.
// Rejecting these keeps one bad point from blowing the map out to world scale and
// skewing the averaged centre.
const IN_INDIA = (lat: number, lng: number) => lat >= 6 && lat <= 38 && lng >= 68 && lng <= 98;
const toLatLng = (p: any): [number, number] | null => {
  if (p == null) return null;
  let lat: number, lng: number;
  if (Array.isArray(p) && typeof p[0] === 'number' && typeof p[1] === 'number') {
    const [a, b] = p;
    if (IN_INDIA(a, b)) { lat = a; lng = b; }          // [lat, lng]
    else if (IN_INDIA(b, a)) { lat = b; lng = a; }     // [lng, lat]
    else { lat = a; lng = b; }
  } else if (typeof p === 'object') {
    lat = Number(p.lat ?? p.latitude ?? p.y);
    lng = Number(p.lng ?? p.lon ?? p.long ?? p.longitude ?? p.x);
  } else return null;
  if (isNaN(lat) || isNaN(lng)) return null;
  if (!IN_INDIA(lat, lng)) return null;                 // outside India → junk / emulator default
  return [lat, lng];
};
const pathToLatLngs = (path: any): [number, number][] => {
  let arr = path;
  // The app stores a path's vertices under `coords` (road/pipeline geo-tags) — with
  // `coordinates`/`points`/`path`/`latlngs` as fallbacks for other shapes.
  if (path && !Array.isArray(path)) arr = path.coords || path.coordinates || path.points || path.path || path.latlngs || [];
  if (typeof arr === 'string') { try { arr = JSON.parse(arr); } catch { return []; } }
  if (arr && !Array.isArray(arr)) arr = arr.coords || arr.coordinates || arr.points || arr.path || arr.latlngs || [];
  if (!Array.isArray(arr)) return [];
  return arr.map(toLatLng).filter(Boolean) as [number, number][];
};

// ── Small building blocks ───────────────────────────────────────────────────
const Section: React.FC<{ delay?: number; className?: string; children: React.ReactNode }> = ({ delay = 0, className = '', children }) => {
  const [shown, setShown] = useState(false);
  useEffect(() => { const t = setTimeout(() => setShown(true), delay); return () => clearTimeout(t); }, [delay]);
  return <div className={className} style={{ opacity: shown ? 1 : 0, transform: shown ? 'translateY(0)' : 'translateY(16px)', transition: 'opacity .5s ease, transform .5s ease' }}>{children}</div>;
};

const Card: React.FC<{ className?: string; children: React.ReactNode; hover?: boolean }> = ({ className = '', children, hover }) => (
  <div className={`bg-white rounded-2xl border border-[#E5E7EB] ${hover ? 'transition-transform duration-300 hover:-translate-y-1' : ''} ${className}`} style={{ boxShadow: '0 4px 20px rgba(0,0,0,.05)' }}>{children}</div>
);

const CardTitle: React.FC<{ icon: string; children: React.ReactNode; action?: React.ReactNode }> = ({ icon, children, action }) => (
  <div className="flex items-center justify-between px-5 pt-4 pb-3">
    <div className="flex items-center gap-2"><i className={`${icon} text-[#6D28D9] text-lg`} /><h3 className="text-[15px] font-bold text-[#111827]">{children}</h3></div>
    {action}
  </div>
);

const Bar: React.FC<{ value: number; color?: string; height?: number }> = ({ value, color, height = 8 }) => {
  const [w, setW] = useState(0);
  useEffect(() => { const t = setTimeout(() => setW(clamp(value)), 120); return () => clearTimeout(t); }, [value]);
  return <div className="w-full rounded-full bg-[#F1F5F9] overflow-hidden" style={{ height }}><div className="h-full rounded-full" style={{ width: `${w}%`, background: color || C.info, transition: 'width 1s cubic-bezier(.4,0,.2,1)' }} /></div>;
};

const ScoreRing: React.FC<{ value: number; size?: number; label: string; color: string }> = ({ value, size = 90, label, color }) => {
  const stroke = 9; const r = (size - stroke) / 2; const circ = 2 * Math.PI * r;
  const [offset, setOffset] = useState(circ);
  useEffect(() => { const t = setTimeout(() => setOffset(circ - (clamp(value) / 100) * circ), 150); return () => clearTimeout(t); }, [value, circ]);
  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#F1F5F9" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={offset} style={{ transition: 'stroke-dashoffset 1.1s cubic-bezier(.4,0,.2,1)' }} />
      </svg>
      <div className="absolute flex flex-col items-center"><span className="text-2xl font-bold text-[#111827] leading-none">{clamp(value)}</span><span className="text-[10px] font-semibold mt-0.5" style={{ color }}>{label}</span></div>
    </div>
  );
};

const TrendLines: React.FC<{ months: string[]; series: Array<{ data: number[]; color: string; label: string }> }> = ({ months, series }) => {
  const w = 520, h = 170, padX = 28, padY = 16;
  const x = (i: number) => padX + (i * (w - padX * 2)) / (months.length - 1);
  const y = (v: number) => h - padY - (v / 100) * (h - padY * 2);
  const smooth = (pts: Array<[number, number]>) => {
    if (pts.length < 2) return '';
    let d = `M ${pts[0][0]} ${pts[0][1]}`;
    for (let i = 0; i < pts.length - 1; i++) { const [x0, y0] = pts[i]; const [x1, y1] = pts[i + 1]; const cx = (x0 + x1) / 2; d += ` C ${cx} ${y0}, ${cx} ${y1}, ${x1} ${y1}`; }
    return d;
  };
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto">
      {[0, 25, 50, 75, 100].map((g) => (<g key={g}><line x1={padX} x2={w - padX} y1={y(g)} y2={y(g)} stroke="#F1F5F9" strokeWidth={1} /><text x={4} y={y(g) + 3} fontSize={9} fill="#9CA3AF">{g}%</text></g>))}
      {series.map((s) => { const pts = s.data.map((v, i) => [x(i), y(v)] as [number, number]); return (<g key={s.label}><path d={smooth(pts)} fill="none" stroke={s.color} strokeWidth={2.5} strokeLinecap="round" />{pts.map((p, i) => <circle key={i} cx={p[0]} cy={p[1]} r={3} fill="#fff" stroke={s.color} strokeWidth={2} />)}</g>); })}
      {months.map((m, i) => <text key={m} x={x(i)} y={h - 2} fontSize={9} fill="#9CA3AF" textAnchor="middle">{m}</text>)}
    </svg>
  );
};

const Row: React.FC<{ l: string; v: React.ReactNode; vClass?: string }> = ({ l, v, vClass }) => (
  <div className="flex items-start justify-between gap-3"><span className="text-[12px] text-[#6B7280] shrink-0">{l}</span><span className={`text-[12px] font-semibold text-[#111827] text-right break-words min-w-0 ${vClass || ''}`} title={typeof v === 'string' ? v : undefined}>{v}</span></div>
);

// ── Work Stage Flow (lifecycle stages, mirrors dashboard Stage-wise Work Status)
// The older works in the system are already in execution (under a work schedule);
// their pre-execution stage dates aren't tracked in this application, so we show
// the full lifecycle flow and derive each node's state from the work's current
// stage: stages before it are Completed, the current stage is highlighted, and
// later stages are Pending.
type StageState = 'done' | 'current' | 'pending';
export interface WorkStage { code: string; name: string; icon: string; color: string; state: StageState }

const STAGE_STATE_META: Record<StageState, { color: string; label: string; icon: string }> = {
  done: { color: '#16A34A', label: 'Completed', icon: 'ri-check-line' },
  current: { color: '#2563EB', label: 'Current', icon: 'ri-focus-3-line' },
  pending: { color: '#94A3B8', label: 'Pending', icon: 'ri-time-line' },
};

const StageLegend: React.FC = () => (
  <div className="flex items-center gap-3 flex-wrap">
    {(['done', 'current', 'pending'] as StageState[]).map((k) => (
      <span key={k} className="flex items-center gap-1.5 text-[11px] text-[#6B7280]"><span className="w-2.5 h-2.5 rounded-full" style={{ background: STAGE_STATE_META[k].color }} />{STAGE_STATE_META[k].label}</span>
    ))}
  </div>
);

// Horizontal stage flow rendered in the existing timeline style: circular icon
// nodes joined by a connecting track, coloured by state (done / current /
// pending), with an overall-progress footer. Same content as the dashboard's
// Stage-wise Work Status, just the milestone-timeline look.
const WorkStageFlow: React.FC<{ stages: WorkStage[]; progress?: number; onTrack?: boolean }> = ({ stages, progress = 0, onTrack = true }) => {
  if (!stages.length) return <div className="px-5 pb-6 text-sm text-[#9CA3AF] text-center py-6">No stage information available for this work yet.</div>;
  const pct = clamp(progress);
  return (
    <div className="px-5 pb-5 pt-1 flex flex-col flex-1 min-h-0">
      <div className="flex-1 flex items-center pb-2">
        <div className="grid w-full" style={{ gridTemplateColumns: `repeat(${stages.length}, minmax(0, 1fr))` }}>
          {stages.map((s, i) => {
            const sm = STAGE_STATE_META[s.state];
            const filled = s.state !== 'pending';
            return (
              <div key={s.code} className="relative flex flex-col items-center text-center px-1 pt-2">
                {/* Connecting track to the next node */}
                {i < stages.length - 1 && (
                  <span className="absolute h-[3px] rounded-full" style={{ top: 31, left: '50%', width: '100%', background: s.state === 'done' ? STAGE_STATE_META.done.color : (s.state === 'current' ? `linear-gradient(90deg, ${STAGE_STATE_META.current.color}, #E5E7EB)` : '#E5E7EB') }} />
                )}
                <span className="relative z-[1] flex items-center justify-center rounded-full" style={{ width: 48, height: 48, background: filled ? sm.color : '#fff', border: filled ? `2px solid ${sm.color}` : '2px solid #CBD5E1', color: filled ? '#fff' : '#94A3B8', boxShadow: s.state === 'current' ? `0 0 0 5px ${sm.color}22` : 'none' }}>
                  <i className={`${s.icon} text-[20px]`} />
                </span>
                <span className="mt-3 text-[12px] font-semibold leading-tight" style={{ color: filled ? '#111827' : '#9CA3AF' }}>{s.name}</span>
                <span className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold" style={{ color: sm.color }}>
                  <i className={sm.icon} />{sm.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Overall progress footer */}
      <div className="mt-4 flex items-center gap-4 rounded-xl border border-[#E5E7EB] bg-[#F8FAFC] px-4 py-3">
        <span className="flex items-center gap-2 shrink-0">
          <span className="w-8 h-8 rounded-lg bg-[#EDE9FE] text-[#6D28D9] flex items-center justify-center"><i className="ri-bar-chart-2-line text-[15px]" /></span>
          <span className="leading-tight"><span className="block text-[10px] text-[#6B7280]">Overall Progress</span><span className="block text-[15px] font-bold text-[#111827]">{pct}%</span></span>
        </span>
        <div className="flex-1"><Bar value={pct} color={onTrack ? C.success : C.danger} height={8} /></div>
        <span className="shrink-0 inline-flex items-center gap-1.5 text-[12px] font-semibold" style={{ color: onTrack ? C.success : C.danger }}>
          {onTrack ? 'On Track' : 'Needs Attention'}<i className={`${onTrack ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'} text-[14px]`} />
        </span>
      </div>
    </div>
  );
};

// ── Embedded OpenStreetMap (Leaflet) ────────────────────────────────────────
interface GeoPhoto { id: number; lat: number; lng: number; label: string; url?: string }
const MiniMap: React.FC<{ lat: number; lng: number; zoom: number; approximate: boolean; approxLabel?: string; height?: number; geometries: any[]; photos: GeoPhoto[]; focus?: { lat: number; lng: number; label?: string; url?: string; exact?: boolean } | null }> = ({ lat, lng, zoom, approximate, approxLabel, height = 220, geometries, photos, focus }) => {
  const elRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);

  useEffect(() => {
    if (!elRef.current || mapRef.current) return;
    const map = L.map(elRef.current, { zoomControl: true, attributionControl: false }).setView([lat, lng], zoom);
    // OpenStreetMap base layer (same provider as the work-profile Map tab).
    L.tileLayer(TILE_URLS.osm, { maxZoom: 19 }).addTo(map);
    if (!approximate) {
      geometries.forEach((g) => {
        const parsed = parseGeom(g.geomData ?? g);
        if (!parsed) return;
        try { L.geoJSON(parsed, { style: { color: g.geomColor || '#8B5CF6', weight: 3, fillOpacity: 0.15 } }).addTo(map); } catch { /* skip */ }
      });
      photos.forEach((p) => {
        const iconClass = getCustomIconOrEmoji(p.label, 'Photo');
        const customIcon = createMapMarkerIcon(iconClass, '#6D28D9', 24);
        const cm = L.marker([p.lat, p.lng], { icon: customIcon }).addTo(map);
        cm.bindPopup(`<div style="font:12px sans-serif">${p.url ? `<img src="${p.url}" style="width:160px;height:100px;object-fit:cover;border-radius:6px"/><br/>` : ''}${p.label || 'Progress photo'}</div>`);
      });
      markerRef.current = L.marker([lat, lng]).addTo(map);
      const allPts: L.LatLngExpression[] = [[lat, lng], ...photos.map((p) => [p.lat, p.lng] as L.LatLngExpression)];
      if (allPts.length > 1) { try { map.fitBounds(L.latLngBounds(allPts as any).pad(0.3)); } catch { /* keep */ } }
    }
    mapRef.current = map;
    setTimeout(() => map.invalidateSize(), 200);
    return () => { map.remove(); mapRef.current = null; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lng, zoom, approximate, geometries.length, photos.length]);

  useEffect(() => {
    if (!mapRef.current || !focus) return;
    const map = mapRef.current;
    map.flyTo([focus.lat, focus.lng], focus.exact ? 18 : 14, { duration: 0.8 });
    // Create the pin lazily so it also works when the map started in approximate mode.
    if (!markerRef.current) markerRef.current = L.marker([focus.lat, focus.lng]).addTo(map);
    const note = focus.exact === false ? '<div style="margin-top:4px;color:#B45309;font-size:10px">Approx — work location (photo has no GPS)</div>' : '';
    markerRef.current.setLatLng([focus.lat, focus.lng]).bindPopup(`<div style="font:12px sans-serif">${focus.url ? `<img src="${focus.url}" style="width:160px;height:100px;object-fit:cover;border-radius:6px"/><br/>` : ''}${focus.label || 'Photo location'}${note}</div>`).openPopup();
  }, [focus]);

  return (
    <div className="relative rounded-xl overflow-hidden">
      <div ref={elRef} style={{ height, width: '100%' }} />
      {approximate && (
        <span className="absolute top-2 left-2 z-[400] px-2 py-1 rounded-md bg-black/55 text-white text-[10px] font-medium">
          <i className="ri-information-line mr-1" />{approxLabel || 'Approximate area — precise GPS not set'}
        </span>
      )}
    </div>
  );
};

// ── Full "larger map" modal — the field mapping captured in the mobile app ───
// Read-only, work-scoped view that overlays EVERYTHING the app recorded on the
// ground for THIS work: per-component geo-tags (points → pins, paths → lines),
// reference/progress geometries, and geo-stamped site photos. This replaces the
// old "View Larger Map" behaviour (which jumped to the record GIS *editor*, a
// heavy screen that didn't even render the mobile geo-tags).
interface FullMapPhoto { lat: number; lng: number; label?: string; url?: string }
const escHtml = (s: any) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));

// What KIND of thing a geo-tag represents — a road, a pipeline, a plain path, or a
// single point. Read from the path object's `path_type`/`pipeline_type`, falling
// back to the geo-tag's geo_type.
const geoKind = (geoType?: string | null, p?: any): string => {
  const pt = String((p && (p.path_type || p.pipeline_type)) || '').toUpperCase();
  if (pt.includes('PIPE')) return 'Pipeline';
  if (pt.includes('ROAD')) return 'Road';
  if (pt.includes('DRAIN')) return 'Drain';
  if (String(geoType || '').toUpperCase() === 'PATH') return 'Path';
  return 'Point';
};
const fmtLen = (v: any, uom?: any) => (v != null && v !== '' && !isNaN(Number(v)) ? `${Number(v).toFixed(0)} ${uom || 'm'}` : null);
// A compact info card used for BOTH the hover tooltip and the click popup, so a user
// sees "what this is for" (component/sub-sector, road/pipeline, length, address) just
// by hovering. Fields stack (muted label ABOVE value, left-aligned, full width) so
// long values — a component description or a full address — wrap cleanly instead of
// squeezing into a narrow right-hand column. `rows` are [label, value]; blanks drop.
const infoCard = (title: string, kind: string | null, rows: Array<[string, any]>, imgUrl?: string): string => {
  const badge = kind ? `<span style="display:inline-block;background:#EDE9FE;color:#6D28D9;font-weight:700;border-radius:5px;padding:1px 7px;font-size:10px;vertical-align:middle;white-space:nowrap">${escHtml(kind)}</span>` : '';
  const body = rows
    .filter((r) => r && r[1] != null && r[1] !== '')
    .map((r) => `<div style="margin-top:6px"><div style="color:#9CA3AF;font-size:9px;font-weight:700;letter-spacing:.04em;text-transform:uppercase">${escHtml(r[0])}</div><div style="color:#1F2937;font-size:12px;font-weight:500;line-height:1.4;margin-top:1px">${escHtml(r[1])}</div></div>`)
    .join('');
  return `<div style="font-family:system-ui,sans-serif;width:230px">${imgUrl ? `<img src="${escHtml(imgUrl)}" style="width:100%;height:120px;object-fit:cover;border-radius:6px;margin-bottom:8px"/>` : ''}<div style="display:flex;align-items:center;gap:6px;flex-wrap:wrap">${badge}</div><div style="font-weight:700;color:#111827;font-size:13px;line-height:1.35;margin-top:${kind ? 4 : 0}px">${escHtml(title)}</div>${body}</div>`;
};

// A normalized geo-tag group: everything the field team recorded for ONE component /
// sub-sector — any number of point locations and drawn paths (roads / pipelines).
interface TagGroup {
  key: string;
  component: string;
  subsector?: string;
  subcomponent?: string;
  points: Array<{ lat: number; lng: number; address?: string; subsector?: string; subcomponent?: string }>;
  paths: Array<{ coords: [number, number][]; length_nu?: number | null; length_uom?: string | null; path_type?: string | null; width_m?: number | null; subsector?: string; subcomponent?: string }>;
}
// One distinct color per component/sub-sector, so each is easy to tell apart on the
// map and in the side list.
const TAG_PALETTE = ['#2563EB', '#16A34A', '#DB2777', '#D97706', '#7C3AED', '#0891B2', '#DC2626', '#4D7C0F', '#9333EA', '#0D9488', '#C2410C', '#4F46E5'];
const tagColor = (i: number) => TAG_PALETTE[i % TAG_PALETTE.length];

// Helper to choose an icon or emoji matching the sub-component item name or description
const getCustomIconOrEmoji = (name?: string | null, kind?: string | null, fallbackName?: string | null): string => {
  const n = `${name || ''} ${fallbackName || ''}`.toLowerCase();
  const k = (kind || '').toLowerCase();

  // 1. Specific Water & Storage Sub-Components
  if (/tank|sump|storage/i.test(n)) return '🛢️';
  if (/ohsr|overhead|elsr|elevated/i.test(n)) return '🗼';
  if (/glsr|ground level/i.test(n)) return '🛢️';
  if (/reservoir/i.test(n)) return '🌊';
  if (/intake|well|draw off/i.test(n)) return '⛲';
  if (/bund/i.test(n)) return '⛰️';

  // 2. Specific Building & Structure Sub-Components
  if (/office/i.test(n)) return '🏬';
  if (/compound wall|wall/i.test(n)) return '🧱';
  if (/shed/i.test(n)) return '🛖';
  if (/quarter/i.test(n)) return '🏠';
  if (/structure/i.test(n)) return '🏗️';
  if (/building/i.test(n)) return '🏢';

  // 3. Treatment Plants & Infrastructure
  if (/wtp|stp|plant|pump house|substation|treatment|pump/i.test(n)) return 'ri-contrast-drop-2-fill';

  // 4. Roads, Paths, Pipelines, Drains, Photos
  if (/road|path|street|lane|pavement|highway/i.test(n) || k === 'road' || k === 'path') return 'ri-road-map-fill';
  if (/pipe|pipeline|main|feeder|network|drain/i.test(n) || k === 'pipeline' || k === 'drain') return 'ri-git-commit-fill';
  if (k === 'photo') return 'ri-camera-fill';

  return 'ri-map-pin-2-fill';
};

// Create a custom Leaflet divIcon with a RemixIcon or Emoji directly on the map (no background circle)
const createMapMarkerIcon = (iconOrEmoji: string, color: string, size = 30) => {
  const half = Math.round(size / 2);
  const content = iconOrEmoji.startsWith('ri-')
    ? `<i class="${iconOrEmoji}" style="font-size:${size}px;color:${color};filter:drop-shadow(0 2px 5px rgba(0,0,0,0.65))"></i>`
    : `<span style="font-size:${size}px;line-height:1;display:inline-block;filter:drop-shadow(0 2px 5px rgba(0,0,0,0.65))">${iconOrEmoji}</span>`;

  return L.divIcon({
    className: 'custom-map-div-icon',
    html: `<div style="display:flex;align-items:center;justify-content:center;width:${size}px;height:${size}px;background:transparent;border:none">${content}</div>`,
    iconSize: [size, size],
    iconAnchor: [half, half],
    popupAnchor: [0, -half],
  });
};

// Helper for line styles (Roads, Pipelines, Drains, General Paths)
const getPathLineStyle = (kind: string, color: string, pathType?: string | null) => {
  const k = (kind || '').toUpperCase();
  const pt = String(pathType || '').toUpperCase();
  if (k === 'PIPELINE' || pt.includes('PIPE')) {
    return { color: '#0284C7', weight: 5, opacity: 0.95, dashArray: '10, 6' }; // Segments along pipe length
  }
  if (k === 'ROAD' || pt.includes('ROAD')) {
    return { color: '#334155', weight: 6, opacity: 0.9, dashArray: undefined }; // Dark solid road line
  }
  if (k === 'DRAIN' || pt.includes('DRAIN')) {
    return { color: '#0D9488', weight: 4, opacity: 0.9, dashArray: '6, 5' }; // Dashed drain
  }
  return { color, weight: 4, opacity: 0.9 };
};

// Parse a geo-tag "collection" into normalized points + paths. The rich data lives in
// the v2 caption — GEOTAG:{points:[{lat,lng,address}], paths:[{coords,length_nu,…}]} —
// and a single sub-sector row often holds MANY points and paths. Columns (lat/lng,
// path_jn) are used only as a fallback when there is no caption.
const parseTagCollection = (src: { caption?: string; lat?: any; lng?: any; addr?: any; path_jn?: any; length_nu?: any; length_uom?: any; cmpnt_nm?: any; subsector?: any; subcomponent?: any }): { points: TagGroup['points']; paths: TagGroup['paths']; subsector?: string; subcomponent?: string } => {
  const points: TagGroup['points'] = [];
  const paths: TagGroup['paths'] = [];
  const cap = typeof src.caption === 'string' ? src.caption : '';
  let j: any = null;
  if (cap.startsWith('GEOTAG:')) { try { j = JSON.parse(cap.slice('GEOTAG:'.length)); } catch { /* fall through to columns */ } }
  const subsector = j?.subsector || j?.sub_sector || j?.dscn_tx || src.subsector || null;
  const subcomponent = j?.subcomponent || j?.sub_component || j?.cmpnt_nm || src.cmpnt_nm || src.subcomponent || null;

  if (j) {
    (Array.isArray(j.points) ? j.points : []).forEach((p: any) => { const ll = toLatLng(p); if (ll) points.push({ lat: ll[0], lng: ll[1], address: p.address || p.addr_tx, subsector: p.subsector || subsector, subcomponent: p.subcomponent || subcomponent }); });
    (Array.isArray(j.paths) ? j.paths : []).forEach((pa: any) => { const c = pathToLatLngs(pa); if (c.length >= 2) paths.push({ coords: c, length_nu: pa.length_nu ?? pa.edited_length ?? pa.measured_length ?? null, length_uom: pa.length_uom ?? null, path_type: pa.path_type ?? null, width_m: pa.width_m ?? null, subsector: pa.subsector || subsector, subcomponent: pa.subcomponent || subcomponent }); });
  }
  if (!points.length && src.lat != null && src.lng != null) { const ll = toLatLng({ lat: src.lat, lng: src.lng }); if (ll) points.push({ lat: ll[0], lng: ll[1], address: src.addr, subsector, subcomponent }); }
  if (!paths.length && src.path_jn) { const c = pathToLatLngs(src.path_jn); if (c.length >= 2) paths.push({ coords: c, length_nu: src.length_nu ?? null, length_uom: src.length_uom ?? null, subsector, subcomponent }); }
  return { points, paths, subsector, subcomponent };
};

const FullMapModal: React.FC<{
  open: boolean;
  onClose: () => void;
  title?: string;
  center: { lat: number; lng: number };
  approximate: boolean;
  geometries: any[];
  tagGroups: TagGroup[];
  photos: FullMapPhoto[];
  approxPhotos: Array<{ id: number; label?: string; url?: string }>;
  onOpenEditor?: () => void;
  /** The work's owning authority — its outline is drawn under the work as context. */
  udaId?: number | null;
}> = ({ open, onClose, title, center, approximate, geometries, tagGroups, photos, approxPhotos, onOpenEditor, udaId }) => {
  const elRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const tileRef = useRef<L.TileLayer | null>(null);
  const layersRef = useRef<Map<string, L.FeatureGroup>>(new Map()); // layer key → feature group (toggle + focus)
  const [maximized, setMaximized] = useState(false);
  const [baseKey, setBaseKey] = useState<'osm' | 'sat' | 'topo'>('osm');
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const hiddenRef = useRef(hidden); hiddenRef.current = hidden;
  const [panelOpen, setPanelOpen] = useState(true);

  const BASE = {
    osm: { url: TILE_URLS.osm, max: 19, label: 'Map', sub: undefined as any },
    sat: { url: TILE_URLS.esriImagery, max: 19, label: 'Satellite', sub: undefined as any },
    topo: { url: TILE_URLS.openTopo, max: 17, label: 'Terrain', sub: undefined as any },
  };

  const showApprox = !approximate && approxPhotos.length > 0;
  const validPhotos = useMemo(() => photos.filter((p) => toLatLng(p)), [photos]);

  const mapLegendItems = useMemo(() => {
    const items: Array<{ icon: string; name: string; color: string }> = [];
    const seen = new Set<string>();

    tagGroups.forEach((grp, i) => {
      const color = tagColor(i);
      grp.points.forEach((p) => {
        const name = p.subsector || grp.subsector || grp.component;
        if (name && !seen.has(name)) {
          seen.add(name);
          const iconClass = getCustomIconOrEmoji(p.subcomponent || grp.subcomponent || name, 'Point', p.address || name);
          items.push({ icon: iconClass, name, color });
        }
      });
      grp.paths.forEach((pa) => {
        const name = pa.subsector || grp.subsector || grp.component;
        const keyName = `${name}-path`;
        if (name && !seen.has(keyName)) {
          seen.add(keyName);
          const kind = geoKind('PATH', pa);
          const iconClass = kind === 'Pipeline' ? 'ri-git-commit-fill' : kind === 'Road' ? 'ri-road-map-fill' : 'ri-route-fill';
          items.push({ icon: iconClass, name: `${name} (${kind})`, color });
        }
      });
    });

    return items;
  }, [tagGroups]);

  // Reset view options each time the modal (re)opens.
  useEffect(() => { if (open) { setMaximized(false); setHidden(new Set()); setPanelOpen(true); setBaseKey('osm'); } }, [open]);

  // ── Build the map + all layers (one feature-group per component/sub-sector, plus
  //    photos / approx-photos / reference). Grouped so the side list can toggle and
  //    zoom to each. Re-runs on open or when field data lands. ────────────────────
  useEffect(() => {
    if (!open || !elRef.current || mapRef.current) return;
    const map = L.map(elRef.current, { zoomControl: true, attributionControl: false }).setView([center.lat, center.lng], approximate ? 7 : 15);
    const b0 = BASE[baseKey];
    tileRef.current = L.tileLayer(b0.url, { maxZoom: b0.max }).addTo(map);
    const store = new Map<string, L.FeatureGroup>();
    const bounds: L.LatLngExpression[] = [];
    const TIP = { sticky: true, direction: 'top' as const, opacity: 1, className: 'wms-geo-tip' };
    const bind = (layer: any, html: string) => layer.bindTooltip(html, TIP).bindPopup(html);

    // Reference / progress / deviation geometries.
    if (geometries.length) {
      const fg = L.featureGroup();
      geometries.forEach((g) => {
        const parsed = parseGeom(g.geomData ?? g.geom_jn ?? g);
        if (!parsed) return;
        try {
          const name = g.notes || g.gisElementName || g.label || 'Reference geometry';
          const html = infoCard(name, 'Reference', []);
          bind(L.geoJSON(parsed, { style: { color: '#64748B', weight: 3, fillOpacity: 0.12 } }), html).addTo(fg);
          const pts: number[][] = []; collectCoords(parsed, pts);
          pts.forEach(([lng, lat]) => bounds.push([lat, lng]));
        } catch { /* skip malformed */ }
      });
      if (fg.getLayers().length) store.set('reference', fg);
    }

    // One feature-group per component / sub-sector — points as pins, paths as lines,
    // all in that group's own colour.
    tagGroups.forEach((grp, i) => {
      const color = tagColor(i);
      const fg = L.featureGroup();
      grp.paths.forEach((pa) => {
        const kind = geoKind('PATH', pa);
        const style = getPathLineStyle(kind, color, pa.path_type);
        const rows: Array<[string, any]> = [];
        if (grp.subsector || pa.subsector) rows.push(['Sub-Sector', grp.subsector || pa.subsector]);
        rows.push(['Length', fmtLen(pa.length_nu, pa.length_uom)]);
        if (pa.width_m != null) rows.push([kind === 'Pipeline' ? 'Diameter' : 'Width', `${pa.width_m} m`]);
        const html = infoCard(grp.component, kind, rows);
        bind(L.polyline(pa.coords, style), html).addTo(fg);
        pa.coords.forEach((ll) => bounds.push(ll));
      });
      grp.points.forEach((p) => {
        const iconClass = getCustomIconOrEmoji(p.subcomponent || grp.component, 'Point', p.address || grp.subsector);
        const customIcon = createMapMarkerIcon(iconClass, color);
        const rows: Array<[string, any]> = [];
        if (grp.subsector || p.subsector) rows.push(['Sub-Sector', grp.subsector || p.subsector]);
        rows.push(['Address', p.address]);
        const html = infoCard(grp.component, 'Point', rows);
        bind(L.marker([p.lat, p.lng], { icon: customIcon }), html).addTo(fg);
        bounds.push([p.lat, p.lng]);
      });
      if (fg.getLayers().length) store.set(`g:${grp.key}`, fg);
    });

    // Geo-stamped site photos (real GPS).
    if (validPhotos.length) {
      const fg = L.featureGroup();
      validPhotos.forEach((p) => {
        const ll = toLatLng(p); if (!ll) return;
        const iconClass = getCustomIconOrEmoji(p.label, 'Photo');
        const customIcon = createMapMarkerIcon(iconClass, '#DB2777');
        const html = infoCard(p.label || 'Site photo', 'Photo', [], p.url);
        bind(L.marker(ll, { icon: customIcon }), html).addTo(fg);
        bounds.push(ll);
      });
      if (fg.getLayers().length) store.set('photos', fg);
    }

    // Gallery photos with NO GPS — approximate, fanned out around the work location.
    if (showApprox) {
      const fg = L.featureGroup();
      const R = 0.0016;
      approxPhotos.forEach((p, i) => {
        const ang = i * 2.399963;
        const rad = approxPhotos.length > 1 ? R * Math.sqrt((i + 1) / approxPhotos.length) : 0;
        const ll: [number, number] = [center.lat + rad * Math.cos(ang), center.lng + rad * Math.sin(ang)];
        const iconClass = getCustomIconOrEmoji(p.label, 'Photo');
        const customIcon = createMapMarkerIcon(iconClass, '#F59E0B');
        const html = infoCard(p.label || 'Site photo', 'Photo · approx', [['Location', 'Approximate (no GPS in photo)']], p.url);
        bind(L.marker(ll, { icon: customIcon }), html).addTo(fg);
        bounds.push(ll);
      });
      if (fg.getLayers().length) store.set('approx', fg);
    }

    // Add the visible groups (respect any current hidden set), then frame everything.
    store.forEach((fg, key) => { if (!hiddenRef.current.has(key)) fg.addTo(map); });
    layersRef.current = store;
    if (bounds.length) { try { map.fitBounds(L.latLngBounds(bounds).pad(0.2), { maxZoom: 17 }); } catch { /* keep */ } }
    else if (!approximate) { L.marker([center.lat, center.lng]).addTo(map); }

    /*
     * The owning authority's outline, from ds_uda_lst_t.extd_bndry_plgn_jn — the same source
     * the dashboard's UDA layer and the drawing maps use.
     *
     * It answers a question the work's own geometry cannot: where this site sits inside
     * the authority that funds it. It is drawn last and sent to the back so it never
     * covers the work, and it only takes the framing when the work has NO geometry of
     * its own — otherwise a work with a survey point would be zoomed out to the whole
     * district. A work whose authority has no polygon (APCRDA, VK-PCPIR) keeps the
     * headquarters-centre fallback above.
     */
    const udaIdForMap = udaId != null ? Number(udaId) : null;
    if (udaIdForMap != null) {
      fetchUdaBoundary(udaIdForMap)
        .then((feature) => {
          if (!feature || mapRef.current !== map) return;
          const layer = drawUdaBoundary(L, map, feature, { fit: !bounds.length, color: '#0f766e' });
          layer.bringToBack();
          tileRef.current?.bringToBack();
        })
        .catch(() => { /* the map is fully usable without the outline */ });
    }

    mapRef.current = map;
    setTimeout(() => map.invalidateSize(), 150);
    return () => { map.remove(); mapRef.current = null; layersRef.current = new Map(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, geometries.length, tagGroups.length, photos.length, showApprox, approxPhotos.length]);

  // Toggle layer visibility without rebuilding the map (keeps the current zoom).
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    layersRef.current.forEach((fg, key) => {
      const on = !hidden.has(key);
      if (on && !map.hasLayer(fg)) fg.addTo(map);
      else if (!on && map.hasLayer(fg)) map.removeLayer(fg);
    });
  }, [hidden]);

  // Swap the base tile layer.
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    if (tileRef.current) tileRef.current.remove();
    const b = BASE[baseKey];
    tileRef.current = L.tileLayer(b.url, { maxZoom: b.max }).addTo(map);
    tileRef.current.bringToBack();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseKey]);

  // Recompute size when the container grows/shrinks (maximize / panel toggle).
  useEffect(() => {
    if (!mapRef.current) return;
    const t = setTimeout(() => { mapRef.current?.invalidateSize(); }, 90);
    return () => clearTimeout(t);
  }, [maximized, panelOpen]);

  const focusKey = (key: string) => {
    const fg = layersRef.current.get(key); const map = mapRef.current;
    if (!fg || !map) return;
    if (hidden.has(key)) setHidden((h) => { const n = new Set(h); n.delete(key); return n; });
    try { const bnds = fg.getBounds(); if (bnds.isValid()) map.fitBounds(bnds.pad(0.35), { maxZoom: 18 }); } catch { /* keep */ }
  };
  const toggleKey = (key: string) => setHidden((h) => { const n = new Set(h); n.has(key) ? n.delete(key) : n.add(key); return n; });

  // Side-list rows: one per component/sub-sector group + photos / approx / reference.
  const groupRows = tagGroups.map((grp, i) => {
    const len = grp.paths.reduce((s, p) => s + (p.length_nu ? Number(p.length_nu) : 0), 0);
    return {
      key: `g:${grp.key}`, color: tagColor(i), name: grp.component,
      sub: [grp.points.length ? `${grp.points.length} pt` : '', grp.paths.length ? `${grp.paths.length} path` : '', len ? `${len.toFixed(0)} m` : ''].filter(Boolean).join(' · ') || '—',
    };
  });
  const extraRows = [
    validPhotos.length ? { key: 'photos', color: '#DB2777', name: 'Site photos', sub: `${validPhotos.length} with GPS` } : null,
    showApprox ? { key: 'approx', color: '#F59E0B', name: 'Photos · approx.', sub: `${approxPhotos.length} no GPS` } : null,
    geometries.length ? { key: 'reference', color: '#64748B', name: 'Reference geometry', sub: `${geometries.length}` } : null,
  ].filter(Boolean) as Array<{ key: string; color: string; name: string; sub: string }>;

  if (!open) return null;
  const totalFeatures = tagGroups.reduce((n, g) => n + g.points.length + g.paths.length, 0) + validPhotos.length + (showApprox ? approxPhotos.length : 0);
  const nothing = totalFeatures === 0 && !geometries.length;
  const LayerRow: React.FC<{ row: { key: string; color: string; name: string; sub: string }; line?: boolean }> = ({ row, line }) => {
    const on = !hidden.has(row.key);
    return (
      <div className={`group flex items-center gap-2 px-2.5 py-1.5 rounded-lg transition-colors ${on ? 'hover:bg-[#F8FAFC]' : 'opacity-45'}`}>
        <button onClick={() => toggleKey(row.key)} className="shrink-0" title={on ? 'Hide' : 'Show'}>
          {line
            ? <span className="block w-4 rounded-full" style={{ height: 4, background: row.color }} />
            : <span className="block w-3.5 h-3.5 rounded-full border-2 border-white" style={{ background: row.color, boxShadow: '0 0 0 1px #e5e7eb' }} />}
        </button>
        <button onClick={() => focusKey(row.key)} className="min-w-0 flex-1 text-left" title="Zoom to this">
          <div className="text-[12px] font-semibold text-[#111827] truncate">{row.name}</div>
          <div className="text-[10px] text-[#6B7280] truncate">{row.sub}</div>
        </button>
        <button onClick={() => toggleKey(row.key)} className="shrink-0 w-6 h-6 rounded flex items-center justify-center text-[#9CA3AF] hover:text-[#6D28D9]" title={on ? 'Hide' : 'Show'}>
          <i className={on ? 'ri-eye-line' : 'ri-eye-off-line'} />
        </button>
      </div>
    );
  };
  return (
    <div className={`fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 ${maximized ? 'p-0' : 'p-4'}`} onClick={onClose}>
      {/* Let the hover info-card size to its content (Leaflet tooltips default to nowrap). */}
      <style>{`.leaflet-tooltip.wms-geo-tip{white-space:normal;width:auto;max-width:none;padding:10px 12px;border-radius:10px;border:1px solid #E5E7EB;box-shadow:0 6px 20px rgba(0,0,0,.16)}.leaflet-tooltip.wms-geo-tip::before{display:none}`}</style>
      <div className={`bg-white shadow-2xl flex flex-col overflow-hidden ${maximized ? 'w-screen h-screen rounded-none' : 'w-full max-w-6xl h-[85vh] rounded-2xl'}`} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={WMS_POPUP_HEADER}>
          <div className="flex items-center gap-2 min-w-0">
            <button onClick={() => setPanelOpen((p) => !p)} className={WMS_POPUP_CLOSE} title={panelOpen ? 'Hide layers' : 'Show layers'}><i className="ri-list-unordered text-base" /></button>
            <div className={WMS_POPUP_ICON}><i className="ri-map-pin-line" /></div>
            <div className="min-w-0">
              <h3 className={WMS_POPUP_TITLE}>Field Mapping{title ? ` — ${title}` : ''}</h3>
              <p className={WMS_POPUP_SUBTITLE}>Location &amp; geo-tags captured in the mobile app · per work / component / sub-sector</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {/* Base-layer switch */}
            <div className="hidden sm:flex items-center gap-0.5 rounded-full bg-white/15 p-0.5">
              {(['osm', 'sat', 'topo'] as const).map((k) => (
                <button key={k} onClick={() => setBaseKey(k)} className={`h-6 px-2.5 rounded-full text-[11px] font-semibold transition-colors ${baseKey === k ? 'bg-white text-teal-700' : 'text-white/80 hover:text-white hover:bg-white/10'}`}>{BASE[k].label}</button>
              ))}
            </div>
            <button onClick={() => setMaximized((m) => !m)} className={WMS_POPUP_CLOSE} title={maximized ? 'Restore' : 'Maximize'}>
              <i className={`${maximized ? 'ri-fullscreen-exit-line' : 'ri-fullscreen-line'} text-base`} />
            </button>
            <button onClick={onClose} className={WMS_POPUP_CLOSE} title="Close"><i className="ri-close-line text-lg" /></button>
          </div>
        </div>

        {/* Body: side list + map */}
        <div className="flex flex-1 min-h-0">
          {panelOpen && (
            <aside className="w-60 shrink-0 border-r border-[#E5E7EB] flex flex-col bg-white">
              <div className="px-3 py-2 border-b border-[#F1F5F9] flex items-center justify-between">
                <span className="text-[11px] font-bold text-[#6B7280] uppercase tracking-wide">Geo-tag layers</span>
                {(hidden.size > 0) && <button onClick={() => setHidden(new Set())} className="text-[10px] font-semibold text-[#6D28D9]">Show all</button>}
              </div>
              <div className="flex-1 overflow-y-auto p-1.5 space-y-0.5">
                {groupRows.length === 0 && extraRows.length === 0 && (
                  <p className="text-[11px] text-[#9CA3AF] px-2 py-4 text-center">No geo-tags recorded for this work.</p>
                )}
                {groupRows.map((r) => <LayerRow key={r.key} row={r} line={false} />)}
                {extraRows.length > 0 && groupRows.length > 0 && <div className="my-1 border-t border-[#F1F5F9]" />}
                {extraRows.map((r) => <LayerRow key={r.key} row={r} line={r.key === 'reference'} />)}
              </div>
              <div className="px-3 py-2 border-t border-[#F1F5F9] text-[10px] text-[#9CA3AF] leading-relaxed">
                <span className="inline-flex items-center gap-1 mr-2"><span className="w-2.5 h-2.5 rounded-full bg-[#DB2777]" /> Photo</span>
                <span className="inline-flex items-center gap-1 mr-2"><span className="w-2.5 h-2.5 rounded-full bg-[#F59E0B]" /> Approx</span>
                <span className="inline-flex items-center gap-1"><span className="w-3 rounded-full bg-[#64748B]" style={{ height: 4 }} /> Reference</span>
                <div className="mt-1">Hover a feature for details · click a row to zoom.</div>
              </div>
            </aside>
          )}

          <div className="relative flex-1 min-w-0">
            <div ref={elRef} className="w-full h-full" />
            {/* Mobile base-layer switch (header hides it on small screens) */}
            <div className="sm:hidden absolute top-2 right-2 z-[500] flex items-center rounded-lg border border-[#E5E7EB] bg-white overflow-hidden shadow">
              {(['osm', 'sat', 'topo'] as const).map((k) => (
                <button key={k} onClick={() => setBaseKey(k)} className={`px-2 py-1 text-[10px] font-semibold ${baseKey === k ? 'bg-[#6D28D9] text-white' : 'text-[#6B7280]'}`}>{BASE[k].label}</button>
              ))}
            </div>
            {nothing && (
              <div className="absolute inset-0 z-[500] flex flex-col items-center justify-center bg-white/70 text-center pointer-events-none">
                <i className="ri-map-2-line text-4xl text-[#CBD5E1] mb-2" />
                <p className="text-sm text-[#6B7280] font-medium">No field mapping recorded for this work yet.</p>
                <p className="text-[11px] text-[#9CA3AF] mt-1">Geo-tags and geo-stamped photos captured in the mobile app will appear here.</p>
              </div>
            )}
            {approximate && !nothing && (
              <span className="absolute top-2 left-2 z-[500] px-2 py-1 rounded-md bg-black/55 text-white text-[10px] font-medium">
                <i className="ri-information-line mr-1" />Approximate area — precise GPS not set
              </span>
            )}

            {/* Map Legend Overlay (Bottom Right Box) */}
            {mapLegendItems.length > 0 && (
              <div className="absolute bottom-4 right-4 z-[500] w-[240px] bg-white/95 backdrop-blur-md border border-[#E2E8F0] shadow-xl rounded-xl p-2.5 text-xs pointer-events-auto">
                <div className="flex items-center justify-between font-bold text-[#1E293B] text-[10px] uppercase tracking-wider mb-2 border-b border-[#F1F5F9] pb-1.5">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-map-pin-2-fill text-[#6D28D9] text-xs" /> Map Legend
                  </span>
                  <span className="text-[10px] font-normal text-[#94A3B8]">{mapLegendItems.length} items</span>
                </div>
                <div className="space-y-1.5 max-h-[160px] overflow-y-auto pr-1">
                  {mapLegendItems.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-[11px] text-[#334155]">
                      <span className="shrink-0 w-6 h-6 rounded-md bg-[#F8FAFC] border border-[#E2E8F0] flex items-center justify-center text-sm shadow-sm" style={{ color: item.color }}>
                        {item.icon.startsWith('ri-') ? (
                          <i className={item.icon} style={{ color: item.color }} />
                        ) : (
                          <span>{item.icon}</span>
                        )}
                      </span>
                      <span className="font-semibold truncate" title={item.name}>{item.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-5 py-2.5 border-t border-[#E5E7EB] flex-wrap">
          <div className="text-[11px] text-[#6B7280]">
            <b className="text-[#111827]">{tagGroups.length}</b> component{tagGroups.length === 1 ? '' : 's'} · <b className="text-[#111827]">{totalFeatures}</b> geo-tagged feature{totalFeatures === 1 ? '' : 's'}
          </div>
          {onOpenEditor && (
            <button onClick={() => { onClose(); onOpenEditor(); }} className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-[12px] font-semibold text-[#6D28D9] border border-[#E5E7EB] hover:bg-[#F8FAFC] transition-colors">
              <i className="ri-edit-2-line" /> Open GIS editor
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Progress-update image gallery (real site photos) ────────────────────────
const ProgressGallery: React.FC<{ workId?: number | string; onPinPhoto?: (p: { url?: string; label?: string; lat?: number; lng?: number }) => void }> = ({ workId, onPinPhoto }) => {
  const [imgs, setImgs] = useState<Array<{ id: number; url: string; label: string; lat?: number; lng?: number }>>([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState<number | null>(null);
  useEffect(() => {
    if (workId == null) { setLoading(false); return; }
    let cancelled = false; const urls: string[] = [];
    (async () => {
      try {
        const rows = await attachmentApi.listWorkPhotos(workId);
        const top = (rows || []).slice(0, 9);
        const out: Array<{ id: number; url: string; label: string; lat?: number; lng?: number }> = [];
        for (const r of top) {
          if (cancelled) break;
          try {
            const url = await attachmentApi.fetchBlobUrl(r.attchmnt_id); urls.push(url);
            const lat = (r as any).lat_nu != null ? Number((r as any).lat_nu) : undefined;
            const lng = (r as any).lng_nu != null ? Number((r as any).lng_nu) : undefined;
            out.push({ id: r.attchmnt_id, url, label: r.source_label || r.file_nm, lat, lng });
          } catch { /* skip */ }
        }
        if (!cancelled) setImgs(out);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; urls.forEach((u) => URL.revokeObjectURL(u)); };
  }, [workId]);

  return (
    <Card>
      <CardTitle icon="ri-camera-line" action={<span className="text-[11px] text-[#6B7280]">{imgs.length} photos{onPinPhoto ? ' · click to locate' : ''}</span>}>Progress Update Images</CardTitle>
      <div className="px-5 pb-5">
        {loading ? (
          <div className="grid grid-cols-3 gap-2">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="rounded-lg bg-[#F1F5F9] animate-pulse" style={{ height: 96 }} />)}</div>
        ) : imgs.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center py-10 text-[#9CA3AF]"><i className="ri-image-2-line text-3xl mb-2" /><span className="text-sm">No progress photos uploaded yet.</span></div>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {imgs.map((p) => (
              <button key={p.id} type="button"
                onClick={() => { setActiveId(p.id); onPinPhoto?.(p); }}
                className={`rounded-lg overflow-hidden relative group text-left ${activeId === p.id ? 'ring-2 ring-[#6D28D9]' : ''}`} style={{ height: 96 }} title={`${p.label} — click to pin on map`}>
                <img src={p.url} alt={p.label} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                {/* Open the full image in a new tab without triggering the pin. */}
                <a href={p.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="absolute top-1 right-1 w-5 h-5 rounded-md bg-black/55 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" title="Open full image"><i className="ri-external-link-line text-[11px]" /></a>
                <span className="absolute bottom-0 inset-x-0 px-1.5 py-0.5 text-[9px] text-white truncate flex items-center gap-1" style={{ background: 'rgba(0,0,0,.45)' }}><i className="ri-map-pin-2-fill text-[9px]" />{p.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};

// ── Funding matrix (CAPEX/OPEX × source) ────────────────────────────────────
const FundingMatrix: React.FC<{ fund: FundingPattern | null; sanctionedCr: number | null; releasedRatio?: number; onEdit?: () => void }> = ({ fund, sanctionedCr, releasedRatio = 0, onEdit }) => {
  const cell = (v: number) => (v > 0 ? v.toFixed(2) : '–');
  // Only the funded columns are shown — GoI, 15th FC and PPP are hidden.
  const cap = fund ? [num(fund.capex_goap_at), num(fund.capex_ulb_at)] : [0, 0];
  const opx = fund ? [num(fund.opex_goap_at), num(fund.opex_ulb_at)] : [0, 0];
  // Row totals still reflect the full record (incl. hidden sources) so the
  // CAPEX/OPEX/Total column stays accurate.
  const capT = fund ? num(fund.capex_totl_at) : 0;
  const opxT = fund ? num(fund.opex_totl_at) : 0;
  const tot = cap.map((c, i) => c + opx[i]);
  const grand = capT + opxT;
  const cols = ['GoAP', 'UDA Share'];
  const hasData = grand > 0;
  // ── Releases Information ──────────────────────────────────────────────────
  // Funds released-to-date per source, derived from work utilisation (value of
  // work done ÷ project cost). Mirrors the funding matrix structure so the two
  // tables read together. Balance = sanctioned − released for each source.
  const rr = Math.max(0, Math.min(1, releasedRatio));
  // Released amounts mirror the funding pattern BY SOURCE (GoAP / UDA / Total).
  const relCap = cap.map((v) => v * rr);
  const relOpx = opx.map((v) => v * rr);
  const relCapT = capT * rr, relOpxT = opxT * rr, relGrand = grand * rr;
  return (
    <Card className="h-full flex flex-col overflow-hidden">
      <CardTitle icon="ri-pie-chart-2-line" action={
        <span className="text-[11px] font-semibold text-[#6B7280]">AS: {sanctionedCr != null && sanctionedCr > 0 ? `${sanctionedCr.toFixed(2)} Cr` : '—'}</span>
      }>Funding Pattern</CardTitle>
      <div className="flex-1 min-h-0">
      <div className="px-5 pb-3">
        {!hasData ? <div className="py-6 text-center text-sm text-[#9CA3AF]">Funding pattern not configured</div> : (
          <table data-wms-table className="w-full text-[11px] table-fixed">
            <thead>
              <tr className="text-[#6B7280] border-b border-[#E5E7EB]">
                <th className="text-left font-semibold py-1.5">Source</th>
                {cols.map((c) => <th key={c} className="text-right font-semibold py-1.5 px-1">{c}</th>)}
                <th className="text-right font-semibold py-1.5 px-1 bg-[#F8FAFC]">Total</th>
              </tr>
            </thead>
            <tbody className="tabular-nums">
              <tr className="border-b border-[#F1F5F9]">
                <td className="py-1.5 font-semibold text-[#3B82F6]">CAPEX</td>
                {cap.map((v, i) => <td key={i} className="text-right py-1.5 px-1 text-[#374151]">{cell(v)}</td>)}
                <td className="text-right py-1.5 px-1 font-semibold bg-[#F0F6FF]">{cell(capT)}</td>
              </tr>
              <tr className="border-b border-[#F1F5F9]">
                <td className="py-1.5 font-semibold text-[#F59E0B]">OPEX</td>
                {opx.map((v, i) => <td key={i} className="text-right py-1.5 px-1 text-[#374151]">{cell(v)}</td>)}
                <td className="text-right py-1.5 px-1 font-semibold bg-[#FFF8EC]">{cell(opxT)}</td>
              </tr>
              <tr>
                <td className="py-1.5 font-bold text-[#111827]">Total</td>
                {tot.map((v, i) => <td key={i} className="text-right py-1.5 px-1 font-semibold text-[#111827]">{cell(v)}</td>)}
                <td className="text-right py-1.5 px-1 font-bold bg-[#FBF7FF] text-[#6D28D9]">{cell(grand)}</td>
              </tr>
            </tbody>
          </table>
        )}
      </div>

      {/* Releases Information — released vs balance per source (derived from utilisation). */}
      <div className="px-5 pt-1 pb-1 flex items-center gap-2 border-t border-[#F1F5F9]">
        <i className="ri-bank-line text-[#16A34A] text-base" />
        <h4 className="text-[12px] font-bold text-[#111827]">Releases Information</h4>
        <span className="ml-auto text-[10px] font-semibold text-[#16A34A]">Released: {relGrand > 0 ? `${relGrand.toFixed(2)} Cr` : '—'}</span>
      </div>
      <div className="px-5 pb-3">
        {!hasData ? <div className="py-4 text-center text-sm text-[#9CA3AF]">No releases recorded</div> : (
          <table data-wms-table className="w-full text-[11px] table-fixed">
            <thead>
              <tr className="text-[#6B7280] border-b border-[#E5E7EB]">
                <th className="text-left font-semibold py-1.5">Source</th>
                {cols.map((c) => <th key={c} className="text-right font-semibold py-1.5 px-1">{c}</th>)}
                <th className="text-right font-semibold py-1.5 px-1 bg-[#F0FDF4]">Total</th>
              </tr>
            </thead>
            <tbody className="tabular-nums">
              <tr className="border-b border-[#F1F5F9]">
                <td className="py-1.5 font-semibold text-[#3B82F6]">CAPEX</td>
                {relCap.map((v, i) => <td key={i} className="text-right py-1.5 px-1 text-[#374151]">{cell(v)}</td>)}
                <td className="text-right py-1.5 px-1 font-semibold bg-[#F0FDF4] text-[#16A34A]">{cell(relCapT)}</td>
              </tr>
              <tr className="border-b border-[#F1F5F9]">
                <td className="py-1.5 font-semibold text-[#F59E0B]">OPEX</td>
                {relOpx.map((v, i) => <td key={i} className="text-right py-1.5 px-1 text-[#374151]">{cell(v)}</td>)}
                <td className="text-right py-1.5 px-1 font-semibold bg-[#F0FDF4] text-[#16A34A]">{cell(relOpxT)}</td>
              </tr>
              <tr>
                <td className="py-1.5 font-bold text-[#111827]">Total</td>
                {relCap.map((v, i) => <td key={i} className="text-right py-1.5 px-1 font-semibold text-[#111827]">{cell(v + relOpx[i])}</td>)}
                <td className="text-right py-1.5 px-1 font-bold bg-[#DCFCE7] text-[#15803D]">{cell(relGrand)}</td>
              </tr>
            </tbody>
          </table>
        )}
      </div>

      </div>
      <div className="px-5 pb-3 text-right text-[10px] text-[#9CA3AF]">All values in Cr.</div>
    </Card>
  );
};

// ── Main component ───────────────────────────────────────────────────────────
const WorkProfileOverview: React.FC<Props> = ({ workId, data, onOpenFullMap, onEditFunding }) => {
  const f = data.financial;
  const phys = clamp(data.physicalProgress);
  const fin = clamp(data.financialProgress);
  // Per-description financial achieved is the work's financial spread across
  // descriptions in proportion to physical. When the work has NO financial
  // progress yet (fin = 0), the ratio is 0 so nothing is shown — financial must
  // not be fabricated from physical. (The old `: 1` fallback made financial mirror
  // physical whenever the work's physical was 0, showing fake financial progress.)
  const finRatio = phys > 0 ? fin / phys : 0;
  const today = useMemo(() => new Date(), []);

  // ── Funding pattern ─────────────────────────────────────────────────────────
  const [fund, setFund] = useState<FundingPattern | null>(null);
  useEffect(() => {
    if (workId == null) return;
    let cancelled = false;
    fundingPatternApi.getByWorkId(Number(workId)).then((d) => { if (!cancelled) setFund(d); }).catch(() => { /* none */ });
    return () => { cancelled = true; };
  }, [workId]);
  const fundProjectCostCr = fund ? num(fund.totl_prjct_cst_at) : 0;
  // Two forms of the same figure, deliberately.
  //   *Display  — nullable. null means the amount was never recorded, and fmtCr shows
  //               a dash. Folding it to 0 would state "sanctioned nil", which is a
  //               different and wrong claim.
  //   plain     — coalesced to 0, because percentages, bar heights and .toFixed() all
  //               need a number and must not blow up on an unrecorded figure.
  const projectCostCrDisplay: number | null = fundProjectCostCr > 0
    ? fundProjectCostCr
    : ((data.projectCostCr ?? 0) > 0 ? (data.projectCostCr as number) : f.contractValueCr);
  const projectCostCr = projectCostCrDisplay ?? 0;
  // Same display/arithmetic split as projectCostCr: the *Display form keeps null so the
  // card shows a dash, this one coalesces because percentages and bar widths need a number.
  const completedValueCr = f.completedValueCr ?? 0;
  const balanceCr = Math.max(projectCostCr - completedValueCr, 0);
  const balanceCrDisplay: number | null = projectCostCrDisplay === null ? null : balanceCr;
  // Funding Utilization card — Allocated / Released / Utilized / Balance (₹ Cr)
  const allocatedCr = projectCostCr;
  const releasedCr = f.releasedCr && f.releasedCr > 0 ? f.releasedCr : (f.sanctionedCr ?? 0);
  const utilizedCr = completedValueCr;
  const utilizationPct = allocatedCr > 0 ? (utilizedCr / allocatedCr) * 100 : 0;

  // Health score
  const score = clamp(((phys + fin) / 2) * 0.8 + 20);
  const scoreRating = score >= 75 ? 'Excellent' : score >= 60 ? 'Good' : score >= 40 ? 'Fair' : 'At Risk';
  const scoreColor = score >= 70 ? C.success : score >= 50 ? C.warning : C.danger;
  const gap = Math.abs(phys - fin);

  // ── Officers (AE/EE/SE) + package — from the work's package bifurcation groups
  const [officers, setOfficers] = useState<{ package: { code: string; name: string } | null; ee: Array<{ name: string; office?: string }>; se: Array<{ name: string; office?: string }>; contractors: Array<{ name: string }> } | null>(null);
  useEffect(() => {
    if (workId == null) return;
    let cancelled = false;
    worksApi.getOfficers(workId).then((o) => { if (!cancelled) setOfficers(o); }).catch(() => { /* keep custom-field fallback */ });
    return () => { cancelled = true; };
  }, [workId]);
  const eeText = officers?.ee?.length ? officers.ee.map((e) => e.name).filter(Boolean).join(', ') : (data.ee?.name || '');
  const seText = officers?.se?.length ? officers.se.map((e) => e.name).filter(Boolean).join(', ') : (data.se?.name || '');

  // ── BOQ breakdown (real) → components (fallback list) + descriptions ───────
  const [boqComps, setBoqComps] = useState<OverviewComponent[] | null>(null);
  const [boqDescs, setBoqDescs] = useState<RawDesc[]>([]);
  useEffect(() => {
    if (workId == null) return;
    let cancelled = false;
    (async () => {
      // Primary source — the schedule-targets breakdown (descriptions → types →
      // components → planned/achieved %). This is the "as per Work Schedule" data.
      let descsRaw: any[] = [];
      try {
        const bd: any = await worksApi.getBoqBreakdown(workId);
        descsRaw = bd?.descriptions || [];
      } catch { descsRaw = []; }
      if (cancelled) return;
      // Fallback — if the breakdown yields no descriptions (templates/components
      // not configured yet, or the breakdown call failed) but the work still has
      // raw BOQ lines, list those so the Description-wise view shows the work's
      // BOQ descriptions instead of an empty state. Existing breakdown path above
      // is untouched; this only runs when it returns nothing.
      if (descsRaw.length === 0) {
        try {
          const rawBoq = await worksApi.getBoqList(workId);
          if (cancelled) return;
          descsRaw = (rawBoq || []).map((r: any) => ({
            wrk_boq_id: r.wrk_boq_id,
            dscn_tx: r.dscn_tx || r.sub_sctr_nm || `Item ${r.wrk_boq_id}`,
            sub_sctr_nm: r.sub_sctr_nm || '',
            amount_at: r.amount_at,
            types: [], // no component breakdown available for the fallback rows
          }));
        } catch { /* keep empty → genuine empty state */ }
      }
      if (cancelled) return;
      // Flat type list (used for the secondary cost/status widgets + KPI count).
      const flat: OverviewComponent[] = descsRaw.flatMap((d: any) =>
        (d.types || []).map((t: any) => {
          const subs: any[] = t.components || [];
          const sumPlan = subs.reduce((a, c) => a + num(c.plan_pct), 0);
          const sumDone = subs.reduce((a, c) => a + Math.min(num(c.done_pct), num(c.plan_pct) || num(c.done_pct)), 0);
          const progress = sumPlan > 0 ? (sumDone / sumPlan) * 100 : 0;
          const started = !!t.actl_strt_dt || subs.some((c) => num(c.done_pct) > 0);
          return {
            name: t.tmplt_nm || d.dscn_tx || 'Component',
            progress, subCount: subs.length, started,
            startDate: t.strt_dt || '', endDate: t.end_dt || '', actualStart: t.actl_strt_dt || '',
            weightage: num(t.tmplt_pct), plndAmtRupees: t.tmplt_amount_at != null ? num(t.tmplt_amount_at) : null,
          };
        })
      );
      setBoqComps(flat);
      // ── Per-description physical + target — IDENTICAL to the Work Schedule
      // table (PackagesAnalysisGrid.descriptionRows) so the two never mismatch:
      //   • value-weighted by each Type-of-Work's planned amount (tmplt_amount_at)
      //   • Achieved = Σ(typeAmt × done%) ÷ Σ(typeAmt × plan%)
      //   • Target   = Σ(typeAmt × plan% for components whose effective month ≤ now)
      //               ÷ Σ(typeAmt × plan%)   — month INCLUSION, not proration.
      const clampPct = (v: any) => Math.max(0, Math.min(100, Math.round((Number(v) || 0) * 100) / 100));
      const cutoffMonth = new Date().toISOString().slice(0, 7); // YYYY-MM (today)
      const monthInScope = (m: string | null) => !!m && m <= cutoffMonth;
      // Effective scheduled month = later of (component month, type start), then
      // pushed forward by any parallel-work break — matches the WS grid exactly.
      const effMonthYM = (cMnth: any, tStrt: any, brkMonths: any = 0): string | null => {
        const a = cMnth ? String(cMnth).slice(0, 7) : null;
        const b = tStrt ? String(tStrt).slice(0, 7) : null;
        let base = (a && b) ? (a >= b ? a : b) : (a || b || null);
        if (!base) return null;
        const n = Number(brkMonths) || 0;
        if (n > 0) {
          const [y, mo] = base.split('-').map(Number);
          const total = (y * 12 + (mo - 1)) + n;
          base = `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}`;
        }
        return base;
      };
      // One row per BOQ DESCRIPTION (sub-sector), aggregating its types.
      const descs: RawDesc[] = descsRaw.map((d: any) => {
        const types: any[] = d.types || [];
        let totPlanVal = 0, duePlanVal = 0, doneVal = 0, subCount = 0, anyStarted = false;
        let minStart: string | null = null, maxEnd: string | null = null;
        for (const t of types) {
          const typeAmt = Number(t.tmplt_amount_at) || 0;
          for (const c of (t.components || [])) {
            const planVal = typeAmt * clampPct(c.plan_pct) / 100;
            totPlanVal += planVal;
            const m = effMonthYM(c.mnth_dt, t.strt_dt, c.par_brk_mnths);
            if (monthInScope(m)) duePlanVal += planVal;
            doneVal += typeAmt * clampPct(c.done_pct) / 100;
            subCount += 1;
            if (num(c.done_pct) > 0) anyStarted = true;
          }
          if (t.actl_strt_dt) anyStarted = true;
          if (t.strt_dt && (!minStart || t.strt_dt < minStart)) minStart = t.strt_dt;
          if (t.end_dt && (!maxEnd || t.end_dt > maxEnd)) maxEnd = t.end_dt;
        }
        return {
          name: d.dscn_tx || d.sub_sctr_nm || 'Description',
          physical: totPlanVal > 0 ? (doneVal / totPlanVal) * 100 : 0,
          subCount, started: anyStarted, startDate: minStart || '', endDate: maxEnd || '',
          amountRupees: num(d.amount_at),
          wrkBoqId: d.wrk_boq_id != null ? Number(d.wrk_boq_id) : undefined,
          // null when there's no component schedule (e.g. the BOQ-list fallback) →
          // the UI then falls back to the date-window estimate.
          scheduleTarget: totPlanVal > 0 ? (duePlanVal / totPlanVal) * 100 : null,
          planValue: totPlanVal,
        };
      });
      setBoqDescs(descs);
    })();
    return () => { cancelled = true; };
  }, [workId]);

  const srcComponents = (boqComps && boqComps.length) ? boqComps : data.components;
  const components = useMemo(() => srcComponents.map((c, i) => {
    const visual = componentVisual(c.name, i);
    const costCr = c.plndAmtRupees != null && c.plndAmtRupees > 0 ? toCr(c.plndAmtRupees) : projectCostCr * ((c.weightage || 0) / 100);
    const pf = clamp(c.progress);
    return { name: c.name, progress: pf, financial: clamp(pf * finRatio), subCount: c.subCount, started: c.started, startDate: c.startDate, endDate: c.endDate, costCr, icon: visual.icon, img: visual.img };
  }), [srcComponents, projectCostCr, finRatio]);

  // Description cards — physical / financial / timeline (target, achieved, delay).
  const descriptions = useMemo(() => boqDescs.map((d, i) => {
    const visual = componentVisual(d.name, i);
    const pf = clamp(d.physical);
    const endDt = d.endDate ? new Date(d.endDate) : null;
    const delay = endDt && !isNaN(endDt.getTime()) && pf < 100 ? Math.max(daysBetween(endDt, today), 0) : 0;
    // Schedule target % — the progress this description is expected to have
    // reached by today, derived from its scheduled start→end window (linear).
    // Drives the "Target" bar in the Physical/Financial Progress columns so they
    // bifurcate Target vs Achieved per the work schedule (matches the dashboard).
    // If a description has already STARTED but its own scheduled start is missing
    // or still in the future, anchor the window to the work's commencement so the
    // Target shows a real value instead of 0%. Genuinely not-started future items
    // correctly stay at 0% (nothing is due yet).
    const ms = (s?: string) => { if (!s) return null; const t = new Date(s).getTime(); return isNaN(t) ? null : t; };
    const now = today.getTime();
    const dStart = ms(d.startDate);
    const dEnd = ms(d.endDate) ?? ms(data.dates.expectedCompletion);
    let effStart = dStart;
    if ((d.started || pf > 0) && (dStart == null || dStart > now)) effStart = ms(data.dates.startDate) ?? dStart;
    let schedPct: number | null = null;
    if (effStart != null && dEnd != null && dEnd > effStart) {
      schedPct = now <= effStart ? 0 : now >= dEnd ? 100 : Math.round(((now - effStart) / (dEnd - effStart)) * 100);
    }
    // Target bar = schedule-plan target (preferred, mirrors the Work Schedule) and
    // only falls back to the date-window estimate when no component schedule exists.
    const target = d.scheduleTarget != null ? clamp(d.scheduleTarget) : schedPct;
    return {
      name: d.name, physical: pf, financial: clamp(pf * finRatio), subCount: d.subCount,
      started: d.started, startDate: d.startDate, endDate: d.endDate, delay, schedPct, target,
      costCr: toCr(d.amountRupees), icon: visual.icon, wrkBoqId: d.wrkBoqId,
    };
  })
    // Show Started works first, then Not Started (stable within each group).
    .sort((a, b) => Number(b.started) - Number(a.started)), [boqDescs, finRatio, today, data.dates.startDate, data.dates.expectedCompletion]);

  // ── Description-Wise period filter ──────────────────────────────────────────
  // When the user ticks "Current period only", restrict the Description-Wise
  // table (and the PDF, which clones it) to descriptions whose scheduled
  // start–end window overlaps the previous + current + next month. Unticked → all.
  const [descPeriodOnly, setDescPeriodOnly] = useState(false);
  const descPeriodWindow = useMemo(() => {
    const y = today.getFullYear(), m = today.getMonth();
    return {
      from: new Date(y, m - 1, 1).getTime(),               // 1st of the previous month
      to: new Date(y, m + 2, 0, 23, 59, 59, 999).getTime(), // last day of the next month
    };
  }, [today]);
  const visibleDescriptions = useMemo(() => {
    if (!descPeriodOnly) return descriptions;
    const parse = (s?: string) => { if (!s) return null; const d = new Date(s); return isNaN(d.getTime()) ? null : d.getTime(); };
    return descriptions.filter((d) => {
      const s = parse(d.startDate);
      const e = parse(d.endDate);
      if (s == null && e == null) return false;     // no schedule → can't place it in a period
      const start = s ?? e!;
      const end = e ?? s!;
      // Overlap test against the window: start ≤ window.to && end ≥ window.from.
      return start <= descPeriodWindow.to && end >= descPeriodWindow.from;
    });
  }, [descriptions, descPeriodOnly, descPeriodWindow]);

  // ── Effective work dates — fall back to the BOQ schedule targets when the
  // work record has no explicit start / completion date set. ──────────────────
  const schedStartStr = boqDescs.map((d) => d.startDate).filter(Boolean).sort()[0] || '';
  const schedEndStr = boqDescs.map((d) => d.endDate).filter(Boolean).sort().slice(-1)[0] || '';
  const startDateStr = data.dates.startDate || schedStartStr;
  const endDateStr = data.dates.expectedCompletion || schedEndStr;
  const start = startDateStr ? new Date(startDateStr) : null;
  const expected = endDateStr ? new Date(endDateStr) : null;
  const ageDays = start && !isNaN(start.getTime()) ? Math.max(daysBetween(start, today), 0) : 0;
  const ageLabel = ageDays > 0 ? `${Math.floor(ageDays / 30)} Months ${ageDays % 30} Days` : '—';
  const delayDays = expected && !isNaN(expected.getTime()) && phys < 100 ? Math.max(daysBetween(expected, today), 0) : 0;

  // ── Work Stage Flow — the work's lifecycle stage (mirrors the dashboard's ────
  // Stage-wise Work Status). Stage groups follow the fixed app order Proposal →
  // Admin Sanction → Technical Sanction → Tendering → Execution → Completion,
  // with Cancelled as a terminal alternative. The current stage is derived from
  // the work's status: completed/handed-over/closed → Completion, cancelled →
  // Cancelled, everything else → Execution (the works currently in the system
  // are all under a work schedule, i.e. in execution).
  const stageFlow = useMemo<WorkStage[]>(() => {
    const DEFS = [
      { code: 'PRPSL', name: 'Proposal', icon: 'ri-file-list-3-line', color: '#64748B' },
      { code: 'ADMSN', name: 'Admin Sanction', icon: 'ri-user-follow-line', color: '#3B82F6' },
      { code: 'TCSN', name: 'Technical Sanction', icon: 'ri-tools-line', color: '#6366F1' },
      { code: 'TNDRG', name: 'Tendering', icon: 'ri-auction-line', color: '#F59E0B' },
      { code: 'EXECN', name: 'Execution', icon: 'ri-loader-4-line', color: '#F97316' },
      { code: 'CMPLN', name: 'Completion', icon: 'ri-checkbox-circle-line', color: '#22C55E' },
      { code: 'CANCL', name: 'Cancelled', icon: 'ri-close-circle-line', color: '#EF4444' },
    ];
    const s = (data.status || '').toLowerCase();
    const isCancelled = /cancel/.test(s);
    const isCompleted = /complet|handed|hand\s*over|closed|closure/.test(s);
    const currentCode = isCancelled ? 'CANCL' : isCompleted ? 'CMPLN' : 'EXECN';
    // Linear progression excludes the terminal "Cancelled" node.
    const progression = DEFS.filter((d) => d.code !== 'CANCL').map((d) => d.code);
    const curIdx = progression.indexOf(currentCode);
    return DEFS.map((d) => {
      let state: StageState;
      if (d.code === 'CANCL') {
        state = isCancelled ? 'current' : 'pending';
      } else if (isCancelled) {
        state = 'pending';
      } else {
        const idx = progression.indexOf(d.code);
        state = idx < curIdx ? 'done' : idx === curIdx ? 'current' : 'pending';
      }
      return { ...d, state };
    });
  }, [data.status]);

  // Selected DESCRIPTION drives the Physical vs Financial panel. -1 = whole work.
  const [selDesc, setSelDesc] = useState(-1);
  // Long description names clamp to 2 lines; "view more" expands the row inline.
  const [expandedDesc, setExpandedDesc] = useState<Set<number>>(new Set());
  const toggleDescExpand = (i: number) => setExpandedDesc((prev) => { const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n; });
  // Overall Work = value-weighted roll-up of the descriptions (which match the Work
  // Schedule table), so the panel stays consistent with the rows below it and
  // updates from the live schedule progress even when the stored work-level
  // phscl_prgrs_nu is 0/stale. Falls back to the stored work progress when there's
  // no BOQ breakdown to roll up.
  const overall = useMemo(() => {
    let tot = 0, done = 0, due = 0, dueTot = 0;
    for (const d of boqDescs) {
      const w = d.planValue || 0;
      if (w <= 0) continue;
      tot += w;
      done += (d.physical / 100) * w;
      if (d.scheduleTarget != null) { due += (d.scheduleTarget / 100) * w; dueTot += w; }
    }
    return {
      phys: tot > 0 ? clamp((done / tot) * 100) : clamp(data.physicalProgress),
      target: dueTot > 0 ? clamp((due / dueTot) * 100) : null,
    };
  }, [boqDescs, data.physicalProgress]);
  const overallFin = clamp(overall.phys * finRatio);

  const active = selDesc >= 0 && descriptions[selDesc]
    ? { label: descriptions[selDesc].name, phys: descriptions[selDesc].physical, fin: descriptions[selDesc].financial }
    : { label: 'Overall Work', phys: overall.phys, fin: overallFin };
  const activeGap = Math.abs(active.phys - active.fin);

  // ── View-images modal (per description) ────────────────────────────────────
  const [imgModal, setImgModal] = useState<{ open: boolean; title: string; loading: boolean; urls: Array<{ id: number; url: string; label: string }> }>({ open: false, title: '', loading: false, urls: [] });
  const openDescImages = async (descName: string, wrkBoqId?: number) => {
    if (workId == null) return;
    setImgModal({ open: true, title: descName, loading: true, urls: [] });
    try {
      const rows = await attachmentApi.listWorkPhotos(workId);
      // Show ONLY the photos uploaded against THIS description's BOQ line
      // (source = 'boq', ref_id = wrk_boq_id) — not every photo of the work.
      // Fall back to a fuzzy name match only when the description has no BOQ id.
      let matched = (rows || []).filter((r) =>
        wrkBoqId != null && r.source === 'boq' && Number(r.ref_id) === Number(wrkBoqId)
      );
      if (!matched.length && wrkBoqId == null) {
        const key = descName.toLowerCase().split(/[\s/,]+/).filter((w) => w.length > 3);
        matched = (rows || []).filter((r) => {
          const lbl = (r.source_label || r.file_nm || '').toLowerCase();
          return r.source === 'boq' && key.some((k) => lbl.includes(k));
        });
      }
      const list = matched.slice(0, 24);
      const out: Array<{ id: number; url: string; label: string }> = [];
      for (const r of list) {
        try { const url = await attachmentApi.fetchBlobUrl(r.attchmnt_id); out.push({ id: r.attchmnt_id, url, label: r.source_label || r.file_nm }); } catch { /* skip */ }
      }
      setImgModal((m) => ({ ...m, loading: false, urls: out }));
    } catch {
      setImgModal((m) => ({ ...m, loading: false, urls: [] }));
    }
  };
  const closeImgModal = () => { imgModal.urls.forEach((u) => URL.revokeObjectURL(u.url)); setImgModal({ open: false, title: '', loading: false, urls: [] }); };

  /* ── Progress history, documents, BOQ lines — all three replace invented data ──
   *
   * Each of these backs a card that previously had no source and filled the gap
   * itself: the trend chart synthesised a curve, the Document Center hardcoded six
   * labels, and the component card silently showed only the scheduled subset of the
   * BOQ. null = still loading, [] = genuinely nothing recorded. The distinction
   * matters: "loading" must not render as "none". */
  const [progressHistory, setProgressHistory] = useState<any[] | null>(null);
  const [workDocs, setWorkDocs] = useState<any[] | null>(null);
  const [boqLines, setBoqLines] = useState<any[] | null>(null);
  useEffect(() => {
    if (workId == null) return;
    let cancelled = false;
    worksApi.getProgress(workId)
      .then((rows: any) => { if (!cancelled) setProgressHistory(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (!cancelled) setProgressHistory([]); });
    worksApi.getDocuments(workId)
      .then((rows: any) => { if (!cancelled) setWorkDocs(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (!cancelled) setWorkDocs([]); });
    // Fetched unconditionally (not just as the breakdown fallback) because the
    // scheduled-vs-BOQ comparison below needs the full BOQ even when the schedule
    // breakdown succeeded.
    worksApi.getBoqList(workId)
      .then((rows: any) => { if (!cancelled) setBoqLines(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (!cancelled) setBoqLines([]); });
    return () => { cancelled = true; };
  }, [workId]);

  // ── Photos (unified count) ──────────────────────────────────────────────────
  const [photoCount, setPhotoCount] = useState(0);
  useEffect(() => {
    if (workId == null) return;
    let cancelled = false;
    attachmentApi.listWorkPhotos(workId).then((rows) => { if (!cancelled) setPhotoCount((rows || []).length); }).catch(() => { });
    return () => { cancelled = true; };
  }, [workId]);

  // ── GIS — geometries + geo-tagged progress photos + mobile field mapping ────
  const [geometries, setGeometries] = useState<any[]>([]);
  const [geoPhotos, setGeoPhotos] = useState<GeoPhoto[]>([]);
  const [mobilePhotos, setMobilePhotos] = useState<GeoPhoto[]>([]); // geo-stamped site photos from the app
  const [tagGroups, setTagGroups] = useState<TagGroup[]>([]);   // per component/sub-sector: every point + path captured in the app
  const [gpsPhotos, setGpsPhotos] = useState<GeoPhoto[]>([]);   // component photos that carry real GPS
  const [approxPhotos, setApproxPhotos] = useState<Array<{ id: number; label?: string; url?: string }>>([]); // gallery photos with no GPS → pinned approximate at the work location
  const [showFullMap, setShowFullMap] = useState(false);
  useEffect(() => {
    if (workId == null) return;
    let cancelled = false;
    workGisApi.getGeometries(workId).then((g: any) => { if (!cancelled) setGeometries(Array.isArray(g) ? g : []); }).catch(() => { });
    workGisApi.getMobilePhotos(workId).then((rows: any) => {
      if (cancelled) return;
      const ph: GeoPhoto[] = [];
      (Array.isArray(rows) ? rows : []).forEach((p: any) => {
        if (p.lat != null && p.lng != null) ph.push({ id: p.id, lat: Number(p.lat), lng: Number(p.lng), label: p.file_nm || 'Site photo', url: /^https?:\/\//.test(p.url || '') ? p.url : undefined });
      });
      setMobilePhotos(ph);
    }).catch(() => { });
    workGisApi.getProgress(workId).then((entries: any) => {
      if (cancelled) return;
      const ph: GeoPhoto[] = [];
      (Array.isArray(entries) ? entries : []).forEach((e: any) => {
        (e.photos || []).forEach((p: any) => {
          if (p.lat != null && p.lng != null) ph.push({ id: p.photoId, lat: p.lat, lng: p.lng, label: p.caption || e.activityName || p.location || 'Progress photo', url: p.thumbnailUrl || p.s3Url });
        });
      });
      setGeoPhotos(ph);
    }).catch(() => { });
    return () => { cancelled = true; };
  }, [workId]);

  // Field geo-tags grouped per component / sub-sector. Two sources, both keyed to a
  // BOQ sub-sector: (a) wms_wrk_gis_rel_t GEOTAG rows, (b) component-image rows whose
  // caption/columns carry a geo-tag collection (many points + paths per row). A NON-
  // marker component photo that has real GPS becomes a located site photo instead.
  useEffect(() => {
    if (workId == null) return;
    let cancelled = false; const blobUrls: string[] = [];
    (async () => {
      const [tagsRaw, imgsRaw, rawBoq] = await Promise.all([
        workGisApi.getGeoTags(workId).catch(() => []),
        workGisApi.getComponentImages(workId).catch(() => []),
        worksApi.getBoqList(workId).catch(() => []),
      ]);
      const boqMap = new Map<number, { subsector?: string; desc?: string }>();
      (Array.isArray(rawBoq) ? rawBoq : []).forEach((b: any) => {
        if (b.wrk_boq_id) boqMap.set(Number(b.wrk_boq_id), { subsector: b.sub_sctr_nm || b.subsector_nm, desc: b.dscn_tx });
      });

      const groups: TagGroup[] = [];
      (Array.isArray(tagsRaw) ? tagsRaw : []).forEach((g: any, i: number) => {
        const bMatch = boqMap.get(Number(g.gis_elmt_id || g.wrk_boq_id));
        const subsector = g.subsector_nm || g.sub_sec_nm || bMatch?.subsector || g.schme_nm || null;
        const subcomponent = g.subcmpnt_nm || (g.cmpnt_nm && g.cmpnt_nm !== bMatch?.desc ? g.cmpnt_nm : null) || bMatch?.subsector || 'Component';
        const points = (Array.isArray(g.points) ? g.points : []).map((p: any) => { const ll = toLatLng(p); return ll ? { lat: ll[0], lng: ll[1], address: p.address || p.addr, subsector: p.subsector || subsector, subcomponent: p.subcomponent || subcomponent } : null; }).filter(Boolean) as TagGroup['points'];
        const paths = (Array.isArray(g.paths) ? g.paths : []).map((pa: any) => { const c = pathToLatLngs(pa); return c.length >= 2 ? { coords: c, length_nu: pa.length_nu ?? g.length_nu ?? null, length_uom: pa.length_uom ?? g.length_uom ?? null, path_type: pa.path_type ?? null, width_m: pa.width_m ?? null, subsector: pa.subsector || subsector, subcomponent: pa.subcomponent || subcomponent } : null; }).filter(Boolean) as TagGroup['paths'];
        if (!points.length && !paths.length) { const ll = toLatLng(g); if (ll) points.push({ lat: ll[0], lng: ll[1], address: g.address, subsector, subcomponent }); }
        if (points.length || paths.length) groups.push({ key: `gt-${g.wrk_gis_rel_id ?? i}`, component: g.cmpnt_nm || g.gis_elmt_lbl_tx || 'Geo-tag', subsector, subcomponent, points, paths });
      });
      const photos: GeoPhoto[] = [];
      for (const r of (Array.isArray(imgsRaw) ? imgsRaw : [])) {
        if (cancelled) break;
        const cap = typeof r.cptn_tx === 'string' ? r.cptn_tx : '';
        const isMarker = /^geotag/i.test(r.file_nm || '') || cap.startsWith('GEOTAG:');
        if (isMarker) {
          const bMatch = boqMap.get(Number(r.wrk_boq_id));
          const subsector = r.subsector_nm || bMatch?.subsector || null;
          const subcomponent = r.subcmpnt_nm || bMatch?.subsector || r.cmpnt_nm || null;
          const { points, paths } = parseTagCollection({ caption: cap, lat: r.lat, lng: r.lng, addr: r.addr_tx, path_jn: r.path_jn, length_nu: r.length_nu, length_uom: r.length_uom, subsector, subcomponent });
          if (points.length || paths.length) groups.push({ key: `ci-${r.cmpnt_img_id}`, component: r.cmpnt_nm || r.file_nm || 'Geo-tag', subsector, subcomponent, points, paths });
        } else {
          const ll = toLatLng({ lat: r.lat, lng: r.lng });
          if (ll) {
            let url: string | undefined;
            try { url = await attachmentApi.fetchBlobUrl(r.attchmnt_id); blobUrls.push(url); } catch { /* pin without thumbnail */ }
            photos.push({ id: r.cmpnt_img_id, lat: ll[0], lng: ll[1], label: r.cmpnt_nm || (cap && !cap.startsWith('GEOTAG:') ? cap : (r.file_nm || 'Site photo')), url });
          }
        }
      }
      if (!cancelled) { setTagGroups(groups); setGpsPhotos(photos); }
    })();
    return () => { cancelled = true; blobUrls.forEach((u) => URL.revokeObjectURL(u)); };
  }, [workId]);

  // Unified "Progress Update" gallery photos that have NO usable GPS — the same feed
  // the small card map pins on click. On the larger map they're anchored (approximately)
  // at the work location. Photos that DO carry GPS arrive via geoImages/geoPhotos already.
  useEffect(() => {
    if (workId == null) return;
    let cancelled = false; const blobUrls: string[] = [];
    (async () => {
      let rows: any[] = [];
      try { rows = await attachmentApi.listWorkPhotos(workId); } catch { rows = []; }
      const noGps = (Array.isArray(rows) ? rows : []).filter((r: any) => !toLatLng({ lat: r.lat_nu, lng: r.lng_nu }));
      const out: Array<{ id: number; label?: string; url?: string }> = [];
      for (const r of noGps.slice(0, 12)) {
        if (cancelled) break;
        let url: string | undefined;
        try { url = await attachmentApi.fetchBlobUrl(r.attchmnt_id); blobUrls.push(url); } catch { /* pin without thumbnail */ }
        out.push({ id: r.attchmnt_id, label: r.source_label || r.file_nm || 'Site photo', url });
      }
      if (!cancelled) setApproxPhotos(out);
    })();
    return () => { cancelled = true; blobUrls.forEach((u) => URL.revokeObjectURL(u)); };
  }, [workId]);

  // Map centre: explicit point → geometry centroid → first geo photo. When none
  // exists, fall back to a regional view (Andhra Pradesh) so a map still renders.
  // When the work has no survey point, centre on its OWN authority rather than on one
  // point for the whole state. The old constant (15.9129, 79.7400) is the geographic
  // centre of Andhra Pradesh, which put every Rajahmundry work ~300 km away near
  // Giddalur. dswms_uda_map_ctr_t supplies a headquarters coordinate for all 21 UDAs;
  // the state centre survives only for a work with no UDA at all.
  const AP_CENTRE = { lat: 15.9129, lng: 79.7400 };
  const FALLBACK = data.udaCentre
    ? { lat: data.udaCentre.lat, lng: data.udaCentre.lng }
    : AP_CENTRE;
  const fallbackLabel = data.udaCentre
    ? `${data.udaCentre.town || 'UDA'} area — this work has no GPS of its own`
    : 'Approximate area — precise GPS not set';
  const explicitLat = data.location.lat != null && data.location.lat !== '' ? Number(data.location.lat) : null;
  const explicitLng = data.location.lng != null && data.location.lng !== '' ? Number(data.location.lng) : null;
  const realCenter = useMemo(() => {
    if (explicitLat != null && explicitLng != null && !isNaN(explicitLat) && !isNaN(explicitLng)) return { lat: explicitLat, lng: explicitLng };
    const pts: number[][] = [];
    geometries.forEach((g) => collectCoords(parseGeom(g.geomData ?? g), pts));
    if (pts.length) { const avg = pts.reduce((a, p) => [a[0] + p[0], a[1] + p[1]], [0, 0]); return { lat: avg[1] / pts.length, lng: avg[0] / pts.length }; }
    // Field geo-tags: average every point / path vertex the app captured on the ground.
    const tagLL: [number, number][] = [];
    tagGroups.forEach((g) => {
      g.points.forEach((p) => tagLL.push([p.lat, p.lng]));
      g.paths.forEach((pa) => pa.coords.forEach((ll) => tagLL.push(ll)));
    });
    if (tagLL.length) { const avg = tagLL.reduce((a, p) => [a[0] + p[0], a[1] + p[1]], [0, 0]); return { lat: avg[0] / tagLL.length, lng: avg[1] / tagLL.length }; }
    const firstPhoto = [...geoPhotos, ...mobilePhotos, ...gpsPhotos].map((p) => toLatLng(p)).find(Boolean);
    if (firstPhoto) return { lat: firstPhoto[0], lng: firstPhoto[1] };
    return null;
  }, [explicitLat, explicitLng, geometries, geoPhotos, tagGroups, gpsPhotos, mobilePhotos]);
  const approximate = !realCenter;
  const center = realCenter || FALLBACK;
  const mapZoom = approximate ? (data.udaCentre ? 10 : 6) : 15;
  const [mapFocus, setMapFocus] = useState<{ lat: number; lng: number; label?: string; url?: string; exact?: boolean } | null>(null);
  // Clicking a Progress-Update photo pins it on the map beside the gallery. Photos
  // rarely carry their own GPS, so fall back to the work's map centre and flag it
  // as approximate (the popup still shows the image so the user sees what's where).
  const pinPhotoOnMap = (p: { url?: string; label?: string; lat?: number; lng?: number }) => {
    const hasGps = p.lat != null && p.lng != null && !isNaN(Number(p.lat)) && !isNaN(Number(p.lng));
    setMapFocus({ lat: hasGps ? Number(p.lat) : center.lat, lng: hasGps ? Number(p.lng) : center.lng, label: p.label, url: p.url, exact: hasGps });
  };

  /* Monthly progress trend — from recorded reporting periods only.
   *
   * What this replaces:
   *     const months6 = ['Jan','Feb','Mar','Apr','May','Jun'];
   *     const physSeries = months6.map((_, i) => clamp((phys * (i+1)) / months6.length));
   * That took the CURRENT progress and back-filled a straight line across six
   * hardcoded month labels. It was not a trend — it was one number smeared across an
   * invented axis. With phys = 1.08% it drew 0,0,1,1,1,1, which reads as steady
   * month-on-month delivery that no reporting period ever recorded. The labels were
   * fixed Jan..Jun regardless of today's date or the project's own timeline, so a work
   * running Aug-Dec still showed a Jan-Jun history.
   *
   * Now each point is one month that actually has an approved/submitted progress
   * period, keyed on the month the period COVERS (prgrs_to_dt) rather than when the
   * row was typed in, and carrying that period's cumulative %. Months with no report
   * are absent rather than interpolated: a gap in reporting is information, and
   * drawing a line through it would manufacture the same fiction in a subtler form.
   *
   * trendMonths empty => the card renders an explicit empty state. */
  const trendPoints = (() => {
    /* APPROVED periods only.
     *
     * A DRAFT progress entry is somebody's unsubmitted working copy. Plotting it puts
     * an unverified figure on the profile in the same shape and colour as certified
     * progress, and the reader has no way to tell them apart — which is the same class
     * of problem as the fabricated series this replaced, just sourced from a real row.
     * The only progress record in the system today is exactly that case: WRK/2026-27/0008,
     * 20 Aug 2026, 0.00%, sts_cd DRAFT, with neither sbmtd_dt nor apprvd_dt set.
     *
     * Approval is taken from apprvd_dt being present, with the status code as a
     * secondary signal, so a record approved through any path still counts.
     *
     * The API deliberately returns every period regardless of status — the filter
     * belongs here, in the view, so a future "progress awaiting approval" screen can
     * use the same endpoint without loosening this rule. */
    const rows = (progressHistory || []).filter((r: any) =>
      !!r?.approvedDate || ['APPROVED', 'APPRVD'].includes(String(r?.status || '').toUpperCase()));
    const byMonth = new Map<string, number>();
    for (const r of rows) {
      const d = r?.toDate || r?.fromDate || r?.approvedDate || r?.submittedDate;
      if (!d) continue;                       // undated period cannot be placed
      const ym = String(d).slice(0, 7);       // YYYY-MM
      if (!/^\d{4}-\d{2}$/.test(ym)) continue;
      const pct = Number(r?.progressPct);
      if (!Number.isFinite(pct)) continue;    // null = not reported, not 0%
      // Several reports in one month -> keep the highest cumulative figure, which is
      // the position reached by month end.
      byMonth.set(ym, Math.max(byMonth.get(ym) ?? 0, pct));
    }
    return Array.from(byMonth.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .slice(-6)                              // most recent six reported months
      .map(([ym, pct]) => ({
        ym,
        label: new Date(`${ym}-01T00:00:00`).toLocaleString('en-IN', { month: 'short' }),
        value: clamp(pct),
      }));
  })();
  const months6 = trendPoints.map((p) => p.label);
  const physSeries = trendPoints.map((p) => p.value);
  const finSeries = physSeries;

  const startedCount = components.filter((c) => c.started).length;
  const statusDonut = [
    { label: 'Completed', value: components.filter((c) => c.progress >= 100).length, color: C.success },
    { label: 'In Progress', value: components.filter((c) => c.started && c.progress < 100).length, color: C.info },
    { label: 'Not Started', value: components.filter((c) => !c.started).length, color: '#CBD5E1' },
  ].filter((x) => x.value > 0);

  const aiInsights = [
    `Physical progress is ${phys >= fin ? 'ahead of' : 'behind'} financial progress by ${gap}%.`,
    `${startedCount} of ${components.length} components are under execution.`,
    delayDays > 0 ? `Work is ${delayDays} days past the planned completion date.` : 'Work is within the planned timeline.',
    geoPhotos.length ? `${geoPhotos.length} geo-tagged site photos on record.` : (photoCount ? `${photoCount} progress photos on record.` : 'No site photos uploaded yet.'),
    expected ? `Planned completion: ${fmtDate(endDateStr)}.` : 'Completion date not set.',
  ];

  /* Real documents, grouped by their configured type.
   *
   * Replaces a hardcoded six-label list (DPR / Estimates & BOQ / Sanctions /
   * Agreements / Drawings / Bills) that was rendered as plain divs with a hover
   * highlight — no onClick, no href, no counts. It looked like a file browser and
   * did nothing, which is worse than showing nothing: it implies the documents are
   * on file. Grouped so one tile per type shows how many are actually held. */
  const documents = (() => {
    const rows = workDocs || [];
    const byType = new Map<string, { label: string; icon: string; count: number }>();
    for (const d of rows) {
      const label = d?.typeName || d?.docName || 'Document';
      const key = String(d?.typeCode || label);
      const prev = byType.get(key);
      if (prev) prev.count += 1;
      else byType.set(key, { label, icon: d?.icon || 'ri-file-3-line', count: 1 });
    }
    return Array.from(byType.values());
  })();

  /* BOQ items with no Type of Work in the schedule (item 4b).
   *
   * The component cards above are built from the schedule-targets breakdown —
   * BOQ description -> Types of Work -> sub-activities — so they only ever show BOQ
   * items that someone has scheduled. On WRK/2026-27/0008 that was 4 of 6 lines, and
   * the two absent ones accounted for Rs 20,10,000 of a Rs 50,00,000 BOQ (40%),
   * including Bituminous Macadam at Rs 17,10,000 — the single largest item in it.
   *
   * Forcing all six into the card was considered and rejected: the missing two have no
   * Type of Work, hence no planned dates, no weightage and no progress, so they would
   * render at 0% and the status donut would report "6 total, 5 not started" — asserting
   * scheduled work that does not exist. The gap is in the schedule, not the display.
   * So the card is labelled for what it holds and the shortfall is stated beside it,
   * where whoever owns the schedule can see it.
   *
   * Matched on amount because the breakdown exposes a Type's amount but not the
   * wrk_boq_id it came from. Suppressed entirely unless we hold both sides of the
   * comparison, so a work whose Types carry no amounts shows no false shortfall. */
  const unscheduledBoq = (() => {
    if (!boqLines || boqLines.length === 0) return [];
    const scheduled = (components || [])
      .map((c: any) => Number(c?.plndAmtRupees))
      .filter((v) => Number.isFinite(v) && v > 0);
    if (scheduled.length === 0) return [];   // nothing to compare against
    const pool = [...scheduled];
    const out: Array<{ name: string; amount: number }> = [];
    for (const b of boqLines) {
      const amt = Number(b?.amount_at) || 0;
      if (amt <= 0) continue;
      const hit = pool.findIndex((v) => Math.abs(v - amt) < 1);
      if (hit >= 0) pool.splice(hit, 1);     // consume, so duplicates pair up 1:1
      else out.push({ name: b?.dscn_tx || `BOQ item ${b?.wrk_boq_id ?? ''}`, amount: amt });
    }
    return out;
  })();
  const unscheduledTotal = unscheduledBoq.reduce((a, b) => a + b.amount, 0);

  return (
    <div style={{ background: C.bg }} className="w-full">
      <div className="mx-auto w-full" style={{ maxWidth: 1800, padding: 24 }}>
        <div className="flex flex-col" style={{ gap: 24 }}>

          {/* PDF-only title block: "Work Name: <name>". The popup header already
              shows the work name and the action buttons on screen, so there is no
              on-screen banner here. display:none means it isn't a flex item and takes
              no space on screen; the PDF clone (handleDownloadOverviewPdf /
              openOverviewPrint) un-hides it via the [data-print-only] selector. */}
          <div data-print-only style={{ display: 'none', background: C.gradient, borderRadius: 16, padding: '20px 24px' }}>
            <h1 className="text-white font-bold" style={{ fontSize: 28, lineHeight: 1.2 }}>Work Name: {data.workName || data.workCode}</h1>
            <p className="text-white" style={{ fontSize: 16, fontWeight: 500, marginTop: 8 }}>
              {data.workCode}{data.status ? `  ·  ${data.status}` : ''}
            </p>
          </div>

          {/* Three cards: Work Details · Contract · Funding + Releases */}
          <Section delay={60}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
              <Card className="h-full">
                <CardTitle icon="ri-file-text-line">Work Details</CardTitle>
                <div className="px-5 pb-5 space-y-2.5">
                  <Row l="Work Type / Sector" v={data.workType || '—'} />
                  <Row l="Scheme" v={data.scheme || '—'} />
                  <Row l="Status" v={data.status || '—'} />
                  {/* Only rendered when recorded. A deposit work is funded by this
                      authority but executed by another body, and the asset can end up
                      with a third — hiding that would misattribute the work. */}
                  {data.executingAgency && <Row l="Executed By" v={data.executingAgency} />}
                  {data.owningBody && <Row l="Asset Owned By" v={data.owningBody} />}
                  {officers?.package && <Row l="Package" v={`${officers.package.code} — ${officers.package.name}`} />}
                  {data.description && <Row l="Description" v={data.description} />}
                  <div className="pt-2 mt-1 border-t border-[#E5E7EB] space-y-2.5">
                    <Row l="Engineer (AE)" v={data.engineer.name || 'Not Assigned'} vClass={data.engineer.name ? '' : 'text-[#9CA3AF]'} />
                    <Row l="Executive Engineer (EE)" v={eeText || 'Not Assigned'} vClass={eeText ? '' : 'text-[#9CA3AF]'} />
                    <Row l="Superintending Engineer (SE)" v={seText || 'Not Assigned'} vClass={seText ? '' : 'text-[#9CA3AF]'} />
                  </div>
                </div>
              </Card>

              <Card className="h-full">
                <CardTitle icon="ri-file-paper-2-line">Contract Information</CardTitle>
                <div className="px-5 pb-5 space-y-2.5">
                  <Row l="Contractor" v={<ContractorLink id={data.contractor.id} name={data.contractor.name} code={data.contractor.code} fallback="—" />} />
                  <Row l="Work Type" v={data.workType || '—'} />
                  <Row l="Scheme" v={data.scheme || '—'} />
                  <Row l="Sanctioned" v={fmtCr(f.sanctionedCr)} />
                  {data.contractor.phone && <Row l="Phone" v={data.contractor.phone} />}
                  <div className="pt-2 mt-1 border-t border-[#E5E7EB] space-y-2.5">
                    <Row l="Agreement No" v={data.agreement?.no || '—'} vClass={data.agreement?.no ? '' : 'text-[#9CA3AF]'} />
                    <Row l="Agreement Date" v={fmtDate(data.agreement?.date)} vClass={data.agreement?.date ? '' : 'text-[#9CA3AF]'} />
                    <Row l="Agreement End Date" v={fmtDate(data.agreement?.endDate)} vClass={data.agreement?.endDate ? '' : 'text-[#9CA3AF]'} />
                  </div>
                  <div className="pt-2">
                    <div className="flex items-center justify-between text-[11px] mb-1"><span className="text-[#6B7280]">Performance</span><span className="font-bold text-[#16A34A]">{score}%</span></div>
                    <Bar value={score} color={C.success} height={6} />
                  </div>
                </div>
              </Card>

              {/* Funding card sizes to its own content (Funding Pattern + Releases
                  Information) so it never scrolls internally — the row simply grows
                  to fit it. */}
              <FundingMatrix fund={fund} sanctionedCr={f.sanctionedCr} releasedRatio={projectCostCr > 0 ? completedValueCr / projectCostCr : 0} onEdit={onEditFunding} />
            </div>
          </Section>

          {/* Project Health Score */}
          <Section delay={120}>
            <Card className="p-6">
              <div className="flex items-center gap-8 flex-wrap">
                <div className="flex flex-col items-center shrink-0">
                  <ScoreRing value={score} label={scoreRating} color={scoreColor} />
                  <span className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: scoreColor }}><span className="w-2 h-2 rounded-full" style={{ background: scoreColor }} /> {delayDays > 0 ? 'Needs Attention' : 'On Track'}</span>
                  <span className="text-[11px] text-[#6B7280] mt-0.5">Project Health Score</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-4 flex-1 min-w-[280px]">
                  {[
                    { l: 'Project Cost', v: fmtCr(projectCostCrDisplay) },
                    { l: 'Value of Work Done', v: fmtCr(f.completedValueCr), sub: projectCostCr ? `${clamp((completedValueCr / projectCostCr) * 100)}% of TPC` : 'TPC not recorded', c: C.success },
                    { l: 'Paid Amount', v: fmtCr(f.paidCr), sub: projectCostCr ? `${clamp((f.paidCr / projectCostCr) * 100)}% of TPC` : 'TPC not recorded', c: C.info },
                    { l: 'Balance Amount', v: fmtCr(balanceCrDisplay), sub: projectCostCr ? `${clamp((balanceCr / projectCostCr) * 100)}% of TPC` : 'TPC not recorded', c: C.warning },
                    { l: 'Sanctioned', v: fmtCr(f.sanctionedCr) },
                    { l: 'Work Start Date', v: fmtDate(startDateStr) },
                    { l: 'Expected Completion', v: fmtDate(endDateStr) },
                    { l: 'Work Age', v: ageLabel, sub: delayDays > 0 ? `Delay ${delayDays}d` : 'On time', c: delayDays > 0 ? C.danger : C.success },
                  ].map((m) => (
                    <div key={m.l}>
                      <div className="text-[11px] uppercase tracking-wide text-[#6B7280] font-semibold">{m.l}</div>
                      <div className="text-lg font-bold mt-0.5" style={{ color: (m as any).c || C.textPrimary }}>{m.v}</div>
                      {(m as any).sub && <div className="text-[11px] text-[#6B7280]">{(m as any).sub}</div>}
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          </Section>

          {/* Stage-wise Work Status + Physical vs Financial — one row */}
          <Section delay={200}>
            <div className="grid grid-cols-1 xl:grid-cols-[1.6fr_1fr] gap-6 items-stretch">
              <Card className="min-w-0 flex flex-col">
                <CardTitle icon="ri-flow-chart-line" action={<StageLegend />}>Project Timeline</CardTitle>
                <WorkStageFlow stages={stageFlow} progress={overall.phys} onTrack={delayDays <= 0} />
              </Card>

              <Card className="min-w-0">
                <CardTitle icon="ri-bar-chart-2-line" action={selDesc >= 0 ? <button onClick={() => setSelDesc(-1)} className="text-[11px] text-[#6D28D9] font-semibold">↺ Whole work</button> : undefined}>Physical vs Financial</CardTitle>
                <div className="px-5 pb-5 space-y-4 min-w-0">
                  <div className="text-[12px] font-semibold text-[#6D28D9] -mt-1 truncate">{active.label}</div>
                  <div><div className="flex items-center justify-between mb-1"><span className="text-xs text-[#6B7280] font-medium">Physical Progress</span><span className="text-sm font-bold text-[#111827]">{active.phys}%</span></div><Bar value={active.phys} color={C.info} /></div>
                  <div><div className="flex items-center justify-between mb-1"><span className="text-xs text-[#6B7280] font-medium">Financial Progress</span><span className="text-sm font-bold text-[#111827]">{active.fin}%</span></div><Bar value={active.fin} color={C.success} /></div>
                  <div className="flex items-center justify-between rounded-xl px-3 py-2.5" style={{ background: '#F8FAFC', border: `1px solid ${C.border}` }}>
                    <div><div className="text-[11px] text-[#6B7280]">Progress Gap</div><div className="text-xl font-bold text-[#111827]">{activeGap}%</div></div>
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full" style={{ color: activeGap <= 10 ? C.success : C.warning, background: activeGap <= 10 ? '#16A34A1A' : '#F59E0B1A' }}><i className="ri-checkbox-circle-line" /> {activeGap <= 10 ? 'Normal' : 'Watch'}</span>
                  </div>
                  {selDesc < 0 && (
                    <>
                      <TrendLines months={months6} series={[{ data: physSeries, color: C.info, label: 'Physical' }, { data: finSeries, color: C.success, label: 'Financial' }]} />
                      <div className="flex items-center justify-center gap-5 text-xs">
                        <span className="flex items-center gap-1.5 text-[#6B7280]"><span className="w-3 h-1.5 rounded-full" style={{ background: C.info }} /> Physical</span>
                        <span className="flex items-center gap-1.5 text-[#6B7280]"><span className="w-3 h-1.5 rounded-full" style={{ background: C.success }} /> Financial</span>
                      </div>
                    </>
                  )}
                </div>
              </Card>
            </div>
          </Section>

          {/* Progress-update images  +  Location & GIS Information (image-3 style) */}
          <Section delay={240}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
              {/* Left — progress update images (replaces the project timeline) */}
              <ProgressGallery workId={workId} onPinPhoto={pinPhotoOnMap} />

              {/* Right — Location & GIS Information */}
              <Card>
                <CardTitle icon="ri-map-pin-line">Location &amp; GIS Information</CardTitle>
                <div className="px-5 pb-5 grid grid-cols-1 sm:grid-cols-[1.25fr_1fr] gap-4">
                  {/* Map + thumbnails */}
                  <div>
                    <MiniMap lat={center.lat} lng={center.lng} zoom={mapZoom} approximate={approximate} approxLabel={fallbackLabel} height={200} geometries={geometries} photos={geoPhotos} focus={mapFocus} />
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      {(geoPhotos.length ? geoPhotos.slice(0, 3) : []).map((p) => (
                        <button key={p.id} type="button" onClick={() => setMapFocus({ lat: p.lat, lng: p.lng, label: p.label, url: p.url })} className="rounded-lg overflow-hidden relative group" style={{ height: 52 }} title={p.label}>
                          {p.url ? <img src={p.url} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : <div className="w-full h-full" style={{ background: 'linear-gradient(135deg,#E0E7FF,#EDE9FE)' }} />}
                          <i className="ri-map-pin-2-fill absolute top-1 right-1 text-white text-xs drop-shadow group-hover:scale-110 transition-transform" />
                        </button>
                      ))}
                      {geoPhotos.length === 0 && Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className="rounded-lg flex items-center justify-center" style={{ height: 52, background: 'linear-gradient(135deg,#E0E7FF,#EDE9FE)' }}><i className="ri-image-line text-[#8B5CF6]" /></div>
                      ))}
                    </div>
                  </div>
                  {/* Info fields + View Larger Map */}
                  <div className="flex flex-col">
                    <div className="space-y-2">
                      {[
                        { l: 'District', v: data.location.district },
                        { l: 'Municipality', v: data.location.municipality },
                        { l: 'Ward', v: data.location.ward },
                        { l: 'Village', v: data.location.village },
                        { l: 'Latitude', v: realCenter ? `${center.lat.toFixed(6)}° N` : null },
                        { l: 'Longitude', v: realCenter ? `${center.lng.toFixed(6)}° E` : null },
                        { l: 'Nearest Habitation', v: data.location.nearestHabitation || data.location.municipality },
                      ].map((r) => (
                        <div key={r.l} className="flex items-center justify-between gap-2">
                          <span className="text-[11px] text-[#6B7280]">{r.l}</span>
                          <span className="text-[12px] font-semibold text-[#111827] truncate text-right" title={r.v || '—'}>{r.v || '—'}</span>
                        </div>
                      ))}
                    </div>
                    <button onClick={() => setShowFullMap(true)} className="mt-3 inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-[12px] font-semibold text-[#6D28D9] border border-[#E5E7EB] hover:bg-[#F8FAFC] transition-colors">
                      <i className="ri-external-link-line" /> View Larger Map
                    </button>
                  </div>
                </div>
              </Card>
            </div>
          </Section>

          {/* Bottom analytics grid */}
          <Section delay={280}>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
              <Card>
                <CardTitle icon="ri-funds-line">Funding Utilization <span className="text-[12px] font-semibold text-[#6B7280] ml-1">(₹ Cr)</span></CardTitle>
                <div className="px-5 pb-5">
                  {(() => {
                    const fcols = [
                      { label: 'Allocated', value: allocatedCr, grad: 'linear-gradient(180deg,#C7D2FE,#A5B4FC)' },
                      { label: 'Released', value: releasedCr, grad: 'linear-gradient(180deg,#86EFAC,#34D399)' },
                      { label: 'Utilized', value: utilizedCr, grad: 'linear-gradient(180deg,#FDE68A,#FBBF24)' },
                      { label: 'Balance', value: balanceCr, grad: 'linear-gradient(180deg,#BFDBFE,#60A5FA)' },
                    ];
                    const fmax = Math.max(...fcols.map((c) => c.value), 1);
                    return (
                      <>
                        <div className="grid grid-cols-4 gap-2 text-center">
                          {fcols.map((c) => (
                            <div key={c.label}>
                              <div className="text-[11px] text-[#6B7280]">{c.label}</div>
                              <div className="text-[13px] font-bold text-[#111827] whitespace-nowrap">{c.value.toFixed(2)} Cr</div>
                            </div>
                          ))}
                        </div>
                        <div className="grid grid-cols-4 gap-2 items-end mt-2" style={{ height: 110 }}>
                          {fcols.map((c) => (
                            <div key={c.label} className="w-full rounded-xl transition-all" style={{ height: `${Math.max((c.value / fmax) * 100, 6)}%`, background: c.grad }} title={`${c.label}: ${c.value.toFixed(2)} Cr`} />
                          ))}
                        </div>
                        <div className="mt-4">
                          <div className="flex items-center justify-between mb-1.5"><span className="text-[12px] text-[#6B7280]">Utilization</span><span className="text-[15px] font-bold text-[#111827]">{utilizationPct.toFixed(2)}%</span></div>
                          <Bar value={utilizationPct} color={C.purple} height={8} />
                        </div>
                      </>
                    );
                  })()}
                </div>
              </Card>

              <Card>
                {/* "Scheduled Components", not "Component Cost Breakdown". The card is
                    built from the schedule-targets breakdown, so it lists Types of Work
                    that exist in the work schedule — never the full BOQ. The old title
                    implied a complete cost breakdown, which is how 2 of 6 BOQ lines
                    (40% of the value) went unnoticed. */}
                <CardTitle icon="ri-funds-box-line">Scheduled Components</CardTitle>
                {unscheduledBoq.length > 0 && (
                  <div className="mx-5 mb-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2">
                    <div className="flex items-start gap-2">
                      <i className="ri-error-warning-line text-amber-600 text-[13px] mt-0.5 shrink-0" />
                      <div className="min-w-0">
                        <div className="text-[11px] font-semibold text-amber-900">
                          {unscheduledBoq.length} BOQ item{unscheduledBoq.length > 1 ? 's' : ''} not in the work schedule
                          {unscheduledTotal > 0 && <> — {fmtCr(unscheduledTotal / 1e7)}</>}
                        </div>
                        <div className="text-[10px] text-amber-700 mt-0.5 leading-relaxed">
                          {unscheduledBoq.map((b) => b.name).join('; ')}
                        </div>
                        <div className="text-[10px] text-amber-600 mt-1">
                          No Type of Work configured, so these carry no dates or progress and are excluded from the figures above.
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div data-print-limit="10" className="px-5 pb-5 space-y-2.5 max-h-[210px] overflow-y-auto">
                  {components.length === 0 ? <div className="text-sm text-[#9CA3AF] py-4 text-center">—</div> : components.map((c, i) => {
                    const pct = projectCostCr > 0 ? (c.costCr / projectCostCr) * 100 : 0;
                    return (<div key={`${c.name}-${i}`}><div className="flex items-center justify-between text-[11px] mb-1"><span className="text-[#6B7280] truncate pr-2">{c.name}</span><span className="font-semibold text-[#111827] shrink-0">{fmtCr(c.costCr)}</span></div><Bar value={pct} color={C.purple} height={5} /></div>);
                  })}
                </div>
              </Card>

              <Card>
                <CardTitle icon="ri-bar-chart-grouped-line">Monthly Progress Trend</CardTitle>
                <div className="px-5 pb-5">
                  {/* An empty chart is the correct output for a work with no reported
                      periods. The previous version could never reach this branch because
                      it generated its own six points from the current progress figure. */}
                  {trendPoints.length === 0 && progressHistory === null ? (
                    <Skeleton shape="rounded" className="w-full" height={120} />
                  ) : trendPoints.length === 0 ? (
                    <div className="flex flex-col items-center justify-center text-center" style={{ height: 120 }}>
                      <i className="ri-line-chart-line text-[22px] text-[#D1D5DB]" />
                      <div className="text-[12px] text-[#9CA3AF] mt-1.5">No approved progress history available</div>
                      <div className="text-[10px] text-[#C4C8CF] mt-0.5">
                        {progressHistory === null
                          ? null
                          : (progressHistory.length > 0
                              /* Distinguish "nothing recorded" from "recorded but not
                                 approved" — the second is an action for someone, the
                                 first is not. */
                              ? `${progressHistory.length} progress ${progressHistory.length > 1 ? 'periods' : 'period'} recorded, none approved yet.`
                              : 'No progress periods have been reported for this work yet.')}
                      </div>
                    </div>
                  ) : (
                  <div className="flex items-end gap-2" style={{ height: 120 }}>
                    {physSeries.map((v, i) => (
                      <div key={i} className="flex-1 flex flex-col items-center justify-end h-full">
                        <span className="text-[9px] font-bold text-[#6D28D9] mb-1">{v}%</span>
                        <div className="w-full rounded-t-md" style={{ height: `${v}%`, background: 'linear-gradient(180deg,#8B5CF6,#6D28D9)', transition: 'height 1s ease' }} />
                        <span className="text-[9px] text-[#9CA3AF] mt-1">{months6[i]}</span>
                      </div>
                    ))}
                  </div>
                  )}
                </div>
              </Card>

              <Card>
                <CardTitle icon="ri-donut-chart-line">Component Status</CardTitle>
                <div className="px-5 pb-5 flex justify-center">
                  {statusDonut.length === 0 ? <div className="py-8 text-sm text-[#9CA3AF]">—</div> : <DonutChart data={statusDonut} size={150} strokeWidth={26} showCenter centerValue={components.length} centerLabel="Total" legendPosition="bottom" legendColumns={2} compactLegend />}
                </div>
              </Card>

              <Card className="relative overflow-hidden md:col-span-2">
                <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full" style={{ background: 'radial-gradient(circle,#8B5CF655,transparent 70%)' }} />
                <CardTitle icon="ri-sparkling-2-line" action={<span className="text-[10px] font-bold px-2 py-0.5 rounded-full text-white" style={{ background: C.gradient }}>AI</span>}>AI Project Insights</CardTitle>
                <ul className="px-5 pb-5 grid sm:grid-cols-2 gap-x-6 gap-y-2">
                  {aiInsights.map((t, i) => <li key={i} className="flex gap-2 text-[12px] text-[#374151]"><i className="ri-arrow-right-circle-line text-[#8B5CF6] shrink-0 mt-0.5" /><span>{t}</span></li>)}
                </ul>
              </Card>

              <Card className="md:col-span-2">
                <CardTitle icon="ri-folder-3-line">Document Center</CardTitle>
                {/* Real uploads only. The hover highlight is kept ONLY on tiles that
                    represent actual files — the previous version styled six hardcoded
                    labels as interactive when nothing was behind them. */}
                {documents.length === 0 && workDocs === null ? (
                  <div className="px-5 pb-5 grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                    {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} shape="rounded" className="h-14 w-full" />)}
                  </div>
                ) : documents.length === 0 ? (
                  <div className="px-5 pb-5">
                    <div className="flex flex-col items-center justify-center text-center py-6 rounded-xl border border-dashed border-[#E5E7EB]">
                      <i className="ri-folder-open-line text-[22px] text-[#D1D5DB]" />
                      <div className="text-[12px] text-[#9CA3AF] mt-1.5">No documents</div>
                      <div className="text-[10px] text-[#C4C8CF] mt-0.5">
                        Nothing has been uploaded against this work yet.
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="px-5 pb-5 grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                    {documents.map((d) => (
                      <div key={d.label} className="flex items-center gap-2.5 rounded-xl border border-[#E5E7EB] px-3 py-2.5 transition-colors hover:bg-[#F8FAFC]">
                        <i className={`${d.icon} text-[#6D28D9] text-lg`} />
                        <div className="min-w-0">
                          <div className="text-[12px] font-medium text-[#111827] truncate">{d.label}</div>
                          <div className="text-[10px] text-[#6B7280]">{d.count} file{d.count > 1 ? 's' : ''}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          </Section>

          {/* Description Wise Physical Progress — full-width table (last) */}
          <Section delay={320}>
            <Card className="min-w-0">
              <CardTitle icon="ri-stack-line" action={
                <div className="flex items-center gap-3">
                  {/* Interactive filter — hidden from the PDF; the PDF simply reflects
                      whichever rows are visible on screen when it is generated. */}
                  <label data-print-hide className="flex items-center gap-1.5 text-[11px] font-semibold text-[#6D28D9] cursor-pointer select-none" title="Show only descriptions scheduled in the previous, current and next month">
                    <input type="checkbox" checked={descPeriodOnly} onChange={(e) => setDescPeriodOnly(e.target.checked)} className="accent-[#6D28D9] w-3.5 h-3.5" />
                    Current period only
                  </label>
                  <span className="text-[11px] text-[#6B7280]">{descPeriodOnly ? `${visibleDescriptions.length} of ${descriptions.length}` : descriptions.length} descriptions · click to inspect</span>
                </div>
              }>Description Wise Physical Progress</CardTitle>
              {descriptions.length === 0 ? (
                <div className="px-5 pb-6 text-sm text-[#9CA3AF] text-center py-8">No BOQ descriptions found for this work yet.</div>
              ) : visibleDescriptions.length === 0 ? (
                <div className="px-5 pb-6 text-sm text-[#9CA3AF] text-center py-8">No descriptions are scheduled in this period (previous, current or next month). Untick “Current period only” to see all.</div>
              ) : (
                <div className="px-5 pb-5 overflow-x-auto max-h-[640px] overflow-y-auto">
                  <table data-wms-table className="w-full text-[12px] border-collapse min-w-[960px] border border-[#E5E7EB]">
                    <thead className="sticky top-0 z-[1]">
                      <tr className="text-[#4C1D95] bg-[#EDE9FE]">
                        <th className="text-left font-bold py-2.5 px-3 border border-[#D8CFF5]">Description</th>
                        <th className="text-right font-bold py-2.5 px-3 border border-[#D8CFF5] whitespace-nowrap">Project Cost</th>
                        <th className="text-center font-bold py-2.5 px-3 border border-[#D8CFF5] whitespace-nowrap">Status</th>
                        <th className="text-center font-bold py-2.5 px-3 border border-[#D8CFF5] whitespace-nowrap">Target</th>
                        <th className="text-center font-bold py-2.5 px-3 border border-[#D8CFF5] whitespace-nowrap">Achieved</th>
                        <th className="text-center font-bold py-2.5 px-3 border border-[#D8CFF5] whitespace-nowrap">Delay</th>
                        <th className="text-left font-bold py-2.5 px-3 border border-[#D8CFF5] w-[180px]">Physical Progress</th>
                        <th className="text-left font-bold py-2.5 px-3 border border-[#D8CFF5] w-[180px]">Financial Progress</th>
                        <th data-print-hide className="text-center font-bold py-2.5 px-3 border border-[#D8CFF5]">Images</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleDescriptions.map((d, vi) => {
                        // selDesc indexes the full `descriptions` array so the Physical
                        // vs Financial panel keeps working when the list is filtered.
                        const i = descriptions.indexOf(d);
                        const selected = selDesc === i;
                        const expanded = expandedDesc.has(i);
                        const isLong = (d.name || '').length > 80;
                        return (
                          <tr key={`${d.name}-${i}-${vi}`}
                            onClick={() => setSelDesc(selected ? -1 : i)}
                            className={`cursor-pointer transition-colors ${selected ? 'bg-[#F5F3FF]' : 'hover:bg-[#F8FAFC]'}`}>
                            <td className="py-2.5 px-3 align-top border border-[#E5E7EB] min-w-[280px]" style={selected ? { boxShadow: 'inset 3px 0 0 #6D28D9' } : undefined}>
                              <div className="flex items-start gap-2 min-w-0">
                                <span className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5" style={{ background: 'linear-gradient(135deg,#EDE9FE,#F5F3FF)' }}><i className={d.icon} style={{ color: C.purple, fontSize: 15 }} /></span>
                                <div className="min-w-0">
                                  <span className="block font-semibold text-[#111827] leading-snug break-words whitespace-normal" title={d.name}
                                    style={expanded ? undefined : { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{d.name}</span>
                                  {isLong && (
                                    <button type="button" onClick={(e) => { e.stopPropagation(); toggleDescExpand(i); }}
                                      className="mt-0.5 text-[10px] font-bold text-[#6D28D9] hover:underline">
                                      {expanded ? 'view less' : 'view more'}
                                    </button>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="py-2.5 px-3 text-right align-middle border border-[#E5E7EB] whitespace-nowrap text-[12px] font-bold text-[#111827]">{fmtCr(d.costCr)}</td>
                            <td className="py-2.5 px-3 text-center align-middle border border-[#E5E7EB]">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold whitespace-nowrap" style={{ background: d.started ? '#DCFCE7' : '#F1F5F9', color: d.started ? C.success : '#6B7280' }}><span className="w-1.5 h-1.5 rounded-full" style={{ background: d.started ? C.success : '#9CA3AF' }} />{d.started ? 'Started' : 'Not Started'}</span>
                            </td>
                            <td className="py-2.5 px-3 text-center align-middle border border-[#E5E7EB] whitespace-nowrap text-[11px] font-semibold text-[#1E3A8A]">{fmtDate(d.endDate)}</td>
                            <td className="py-2.5 px-3 text-center align-middle border border-[#E5E7EB] whitespace-nowrap">
                              <span className="text-[11px] font-bold tabular-nums" style={{ color: progressColor(d.physical) }}>{d.physical}%</span>
                            </td>
                            <td className="py-2.5 px-3 text-center align-middle border border-[#E5E7EB] whitespace-nowrap">
                              <span className="text-[11px] font-bold" style={{ color: d.delay > 0 ? C.danger : C.success }}>{d.delay > 0 ? `${d.delay}d` : 'On time'}</span>
                            </td>
                            <td className="py-2.5 px-3 align-middle border border-[#E5E7EB] min-w-[170px]">
                              {/* Target (scheduled-by-now %) over Achieved (done %) — bifurcated per the work schedule. */}
                              {renderDualProgress(d.physical, d.target, 'emerald')}
                            </td>
                            <td className="py-2.5 px-3 align-middle border border-[#E5E7EB] min-w-[170px]">
                              {renderDualProgress(d.financial, d.target, 'sky')}
                            </td>
                            <td data-print-hide className="py-2.5 px-3 text-center align-middle border border-[#E5E7EB]">
                              <button type="button" onClick={(e) => { e.stopPropagation(); openDescImages(d.name, d.wrkBoqId); }} className="text-[10px] font-bold inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-white transition-transform hover:scale-105 whitespace-nowrap" style={{ background: 'linear-gradient(135deg,#6D28D9,#8B5CF6)' }}><i className="ri-image-2-line" /> View</button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </Section>

        </div>
      </div>

      {/* View-images modal (per description) */}
      {imgModal.open && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 p-4" onClick={closeImgModal}>
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[88vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className={WMS_POPUP_HEADER}>
              <h3 className={WMS_POPUP_TITLE}><i className="ri-image-2-line text-base text-white/90 mr-1.5" />Images — {imgModal.title}</h3>
              <button onClick={closeImgModal} className={WMS_POPUP_CLOSE}><i className="ri-close-line text-lg" /></button>
            </div>
            <div className="p-5 overflow-y-auto">
              {imgModal.loading ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="rounded-lg bg-[#F1F5F9] animate-pulse" style={{ height: 130 }} />)}</div>
              ) : imgModal.urls.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-[#9CA3AF]"><i className="ri-image-off-line text-4xl mb-2" /><span className="text-sm">No images found for this description.</span></div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {imgModal.urls.map((p) => (
                    <a key={p.id} href={p.url} target="_blank" rel="noreferrer" className="rounded-lg overflow-hidden relative group" style={{ height: 140 }} title={p.label}>
                      <img src={p.url} alt={p.label} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                      <span className="absolute bottom-0 inset-x-0 px-1.5 py-0.5 text-[9px] text-white truncate" style={{ background: 'rgba(0,0,0,.45)' }}>{p.label}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Larger map — the field mapping captured in the mobile app for this work */}
      <FullMapModal
        open={showFullMap}
        onClose={() => setShowFullMap(false)}
        title={data.workName || data.workCode}
        center={center}
        udaId={(data as any)?.udaId ?? null}
        approximate={approximate}
        geometries={geometries}
        tagGroups={tagGroups}
        photos={[...geoPhotos, ...mobilePhotos, ...gpsPhotos]}
        approxPhotos={approxPhotos}
        onOpenEditor={onOpenFullMap}
      />
    </div>
  );
};

export default WorkProfileOverview;
